import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Award,
  Briefcase,
  Calendar,
  Clipboard,
  PauseCircle,
  RefreshCw,
  RotateCcw,
  Send,
  TrendingUp,
  Video,
  XCircle,
  XSquare,
  Zap
} from 'react-feather';
import BunnyLoader from '../components/ui/BunnyLoader';
import { Button } from '../components/ui/button';
import CandidateEvaluationPanel from '../components/Hirelane/CandidateEvaluationPanel';
import ScheduleInterviewModal from '../components/Hirelane/ScheduleInterviewModal';
import InterviewOutcomeModal from '../components/Hirelane/InterviewOutcomeModal';
import CandidateInterviewTimeline from '../components/Hirelane/CandidateInterviewTimeline';
import ClarificationSection from '../components/Hirelane/clarification/ClarificationSection';
import { hirelaneApi, hirelaneClarificationApi } from '../services/api';
import { interviewAttendeesSummary } from '../utils/hirelaneInterviewAttendees';
import { hirelanePipelineStageLabel } from '../utils/hirelaneLabels';
import {
  TAB_IDS,
  candidatesForTab,
  candidatesForTabSorted,
  interviewIsPastDue
} from '../utils/hirelaneWorkflow';
import {
  HIRELANE_REJECT_REASONS,
  HIRELANE_SHORTLIST_REASONS,
  HIRELANE_SHORTLIST_NEXT_STEPS
} from '../utils/hirelaneStatusDecisionOptions';
import './HirelaneJobDetailPage.css';
import './HirelaneJobCreatePage.css';

/** Border + pill colors for pipeline scanability (accessible contrast on light backgrounds). */
const STATUS_ACCENTS = {
  New: { border: '#64748b', pillBg: '#f1f5f9', pillFg: '#334155' },
  'Cony Reviewed': { border: '#2563eb', pillBg: '#eff6ff', pillFg: '#1e40af' },
  'Under Review': { border: '#0369a1', pillBg: '#e0f2fe', pillFg: '#0c4a6e' },
  'Needs Clarification': { border: '#c2410c', pillBg: '#fff7ed', pillFg: '#9a3412' },
  'Hold Profile': { border: '#b45309', pillBg: '#fffbeb', pillFg: '#92400e' },
  Shortlisted: { border: '#16a34a', pillBg: '#f0fdf4', pillFg: '#166534' },
  'Round Cleared': { border: '#059669', pillBg: '#ecfdf5', pillFg: '#065f46' },
  'Hold Pipeline': { border: '#d97706', pillBg: '#fffbeb', pillFg: '#b45309' },
  'Interview Scheduled': { border: '#7c3aed', pillBg: '#f5f3ff', pillFg: '#5b21b6' },
  Rejected: { border: '#dc2626', pillBg: '#fef2f2', pillFg: '#991b1b' },
  Selected: { border: '#ca8a04', pillBg: '#fefce8', pillFg: '#854d0e' }
};

function candidateProfileSourceLine(c) {
  if (c.profile_source === 'vendor') {
    const n = c.hirelane_vendor?.name;
    if (n) return `Via: ${n}`;
    if (c.hirelane_vendor_id) return 'Via: vendor';
    return null;
  }
  if (c.profile_source === 'referral' && c.referrer_name) return `Ref: ${c.referrer_name}`;
  return null;
}

const WORK_MODE_LABELS = {
  office: 'Office',
  remote: 'Remote',
  hybrid: 'Hybrid',
  either: 'Either'
};

function formatWorkModePreference(value) {
  if (!value || typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  return WORK_MODE_LABELS[key] || value.trim();
}

function formatCandidateExperienceShort(c) {
  const t = c.total_experience != null ? String(c.total_experience).trim() : '';
  const r = c.relevant_experience != null ? String(c.relevant_experience).trim() : '';
  if (t && r && r !== t) return `${t} · ${r} rel`;
  return t || r || '';
}

function formatCandidateNoticeShort(c) {
  if (!c.notice_period && !c.notice_period_negotiable && c.currently_on_notice !== true) return '';
  let s = (c.notice_period || '').trim();
  if (c.notice_period_negotiable) s += s ? '†' : 'Neg';
  if (c.currently_on_notice === true) s += s ? ' · live' : 'On NP';
  return s;
}

function truncateFactDisplay(value, maxLen = 11) {
  const s = value != null ? String(value).trim() : '';
  if (!s) return '';
  return s.length > maxLen ? `${s.slice(0, maxLen - 1)}…` : s;
}

/** Six-slot comp snapshot for the card vitals widget (abbr + full title for tooltips). */
function buildCandidateFactCells(c) {
  const workMode = formatWorkModePreference(c.work_mode_preference);
  return [
    { key: 'exp', abbr: 'Exp', title: 'Experience (total · relevant)', value: formatCandidateExperienceShort(c) },
    { key: 'ctc', abbr: 'Now', title: 'Current CTC', value: c.current_ctc },
    { key: 'ectc', abbr: 'Ask', title: 'Expected CTC', value: c.expected_ctc },
    { key: 'np', abbr: 'NP', title: 'Notice period († = negotiable, live = serving notice now)', value: formatCandidateNoticeShort(c) },
    { key: 'loc', abbr: 'Loc', title: 'Current location', value: c.current_location },
    { key: 'mode', abbr: 'Mode', title: 'Work mode preference', value: workMode }
  ];
}

function candidateVendorName(c, vendors) {
  if (c.hirelane_vendor?.name) return c.hirelane_vendor.name;
  if (c.hirelane_vendor_id && vendors?.length) {
    return vendors.find((v) => v.id === c.hirelane_vendor_id)?.name || null;
  }
  return null;
}

function candidateProfileSourceLabel(c, vendors) {
  const src = c.profile_source;
  if (src === 'vendor') {
    const name = candidateVendorName(c, vendors);
    return name ? `Vendor · ${name}` : 'Vendor';
  }
  if (src === 'referral') {
    return c.referrer_name ? `Referral · ${c.referrer_name}` : 'Referral';
  }
  if (src === 'direct') return 'Direct';
  if (c.hirelane_vendor_id) {
    const name = candidateVendorName(c, vendors);
    return name ? `Vendor · ${name}` : 'Vendor';
  }
  if (c.referrer_name) return `Referral · ${c.referrer_name}`;
  return null;
}

function buildCandidateProfileRows(c, vendors) {
  const rows = [];
  const add = (label, value, opts = {}) => {
    const raw = value != null ? String(value).trim() : '';
    if (!raw) return;
    rows.push({ key: label, label, value: raw, ...opts });
  };

  add('Email', c.email, { href: `mailto:${c.email}` });
  add('Phone', c.phone, { href: `tel:${c.phone}` });
  add('Current company', c.current_company);
  add('Current designation', c.current_designation);
  add('Total experience', c.total_experience);
  add('Relevant experience', c.relevant_experience);
  add('Current CTC', c.current_ctc);
  add('Expected CTC', c.expected_ctc);

  if (c.notice_period) {
    let np = c.notice_period;
    if (c.notice_period_negotiable) np += ' (negotiable)';
    if (c.currently_on_notice === true) np += ' · currently serving notice';
    add('Notice period', np);
  } else if (c.notice_period_negotiable) {
    let np = 'Negotiable';
    if (c.currently_on_notice === true) np += ' · currently serving notice';
    add('Notice period', np);
  } else if (c.currently_on_notice === true) {
    add('Notice period', 'Currently serving notice');
  } else if (c.currently_on_notice === false) {
    add('Currently on notice', 'No');
  }

  add('Current location', c.current_location);
  if (c.willing_to_relocate === true) add('Willing to relocate', 'Yes');
  else if (c.willing_to_relocate === false) add('Willing to relocate', 'No');

  const workMode = formatWorkModePreference(c.work_mode_preference);
  if (workMode) add('Work mode preference', workMode);

  add('LinkedIn', c.linkedin_url, { href: c.linkedin_url, external: true });
  add('GitHub', c.github_url, { href: c.github_url, external: true });

  const source = candidateProfileSourceLabel(c, vendors);
  if (source) add('Profile source', source);

  add('Skills', c.skills, { multiline: true });
  add('Notes', c.notes, { multiline: true });

  return rows;
}

function splitLines(text) {
  if (!text || typeof text !== 'string') return [];
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function briefingParagraph(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim();
  return t.length ? t : null;
}

function verdictSnippet(text, maxLen = 140) {
  if (!text || typeof text !== 'string') return '';
  const t = text.trim().replace(/\s+/g, ' ');
  const dot = t.search(/[.!?](?:\s|$)/);
  let snippet = dot >= 0 ? t.slice(0, dot + 1).trim() : t;
  if (snippet.length > maxLen) snippet = `${t.slice(0, maxLen).trim()}…`;
  return snippet;
}

/** Short Cony blurb for collapsed cards — prefers executive summary, then recommendation opener. */
function conyCollapsedSummary(evaluation, maxLen = 220) {
  if (!evaluation) return '';
  const summary = typeof evaluation.evaluation_summary === 'string' ? evaluation.evaluation_summary.trim() : '';
  if (summary) return verdictSnippet(summary, maxLen) || summary;
  const rec = typeof evaluation.recommendation === 'string' ? evaluation.recommendation.trim() : '';
  if (rec) return verdictSnippet(rec, maxLen) || rec;
  return '';
}

function truncateMetaChipLabel(value, maxLen = 28) {
  const s = value != null ? String(value).trim() : '';
  if (!s) return '';
  return s.length > maxLen ? `${s.slice(0, maxLen - 1)}…` : s;
}

/** Compact contact / profile chips for candidate card header. */
function candidateCardMetaChips(c) {
  const chips = [];
  const company = (c.current_company || '').trim();
  if (company) {
    chips.push({
      key: 'company',
      label: truncateMetaChipLabel(company),
      title: company.length > truncateMetaChipLabel(company).length ? company : undefined
    });
  }
  if (c.current_designation) {
    const role = String(c.current_designation).trim();
    chips.push({
      key: 'role',
      label: truncateMetaChipLabel(role, 32),
      title: role.length > truncateMetaChipLabel(role, 32).length ? role : undefined
    });
  }
  if (c.email) chips.push({ key: 'email', label: c.email });
  if (c.phone) chips.push({ key: 'phone', label: c.phone });
  const profile = candidateProfileSourceLine(c);
  if (profile) chips.push({ key: 'profile', label: profile });
  if (c.resume_file_path) chips.push({ key: 'resume', label: 'Resume' });
  return chips;
}

/** Pipeline context chips (shortlisted / interview tabs) — avoids repeating Cony metrics. */
function candidateCardSignalChips(c, summ) {
  const chips = [];
  const activeInv = summ.active_scheduled_interview;
  if (activeInv?.meeting_link && !interviewIsPastDue(activeInv)) {
    chips.push({
      key: 'join-call',
      label: 'Join call',
      href: activeInv.meeting_link,
      variant: 'join'
    });
  }
  if (activeInv && interviewIsPastDue(activeInv)) {
    chips.push({ key: 'pending-result', label: 'Pending result' });
  }
  if (summ.latest_completed_interview) {
    let label = summ.latest_completed_interview.display_round_label;
    if (summ.last_outcome && summ.last_outcome !== 'Pending') label += ` · ${summ.last_outcome}`;
    chips.push({ key: 'last-round', label });
  }
  if (summ.recommended_next_step) {
    chips.push({ key: 'next', label: `Next: ${summ.recommended_next_step}` });
  }
  return chips;
}

/** Single-line preview for collapsed job brief strip */
function jobBriefPreviewLine(draft) {
  const jd = (draft?.final_jd || '').trim().replace(/\s+/g, ' ');
  if (!jd) return 'No job description yet.';
  return jd.length > 160 ? `${jd.slice(0, 157)}…` : jd;
}

function EvaluationReviewDetail({ evaluation }) {
  const raw =
    evaluation.raw_ai_response && typeof evaluation.raw_ai_response === 'object'
      ? evaluation.raw_ai_response
      : {};

  const strengths = splitLines(evaluation.strengths);
  const concerns = splitLines(evaluation.concerns);
  const matching = (evaluation.matching_skills || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const missing = (evaluation.missing_skills || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const caseAdv = briefingParagraph(raw.case_for_advancing);
  const caseDecl = briefingParagraph(raw.case_for_declining);
  const expAlign = briefingParagraph(raw.experience_alignment);
  const interviewFocus = Array.isArray(raw.interview_focus_areas)
    ? raw.interview_focus_areas
        .map((x) => (x == null ? '' : String(x).trim()))
        .filter(Boolean)
    : [];

  return (
    <div className="hirelane-detail__evalFull">
      {evaluation.recommendation && (
        <div
          style={{
            marginBottom: 14,
            padding: 12,
            borderRadius: 6,
            background: 'rgba(25, 80, 180, 0.07)',
            border: '1px solid rgba(25, 80, 180, 0.12)'
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, opacity: 0.9 }}>
            Cony recommendation and reasoning
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{evaluation.recommendation}</div>
        </div>
      )}

      {evaluation.evaluation_summary && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Cony executive summary</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{evaluation.evaluation_summary}</div>
        </div>
      )}

      {caseAdv && (
        <div style={{ marginBottom: 14, paddingLeft: 12, borderLeft: '3px solid #1a7f37' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Why you might shortlist or advance</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{caseAdv}</div>
        </div>
      )}

      {caseDecl && (
        <div style={{ marginBottom: 14, paddingLeft: 12, borderLeft: '3px solid #b35900' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Why you might pass or reject</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{caseDecl}</div>
        </div>
      )}

      {expAlign && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Experience vs job requirements</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{expAlign}</div>
        </div>
      )}

      {interviewFocus.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>If you interview, verify or probe</div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, lineHeight: 1.55 }}>
            {interviewFocus.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {(matching.length > 0 || missing.length > 0) && (
        <div style={{ fontSize: 13, marginBottom: 10, lineHeight: 1.5 }}>
          {matching.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <strong>Matching skills:</strong> {matching.join(', ')}
            </div>
          )}
          {missing.length > 0 && (
            <div>
              <strong>Gaps vs JD:</strong> {missing.join(', ')}
            </div>
          )}
        </div>
      )}

      {(strengths.length > 0 || concerns.length > 0) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            fontSize: 13,
            lineHeight: 1.55
          }}
        >
          {strengths.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Strengths</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {strengths.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {concerns.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Concerns</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {concerns.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function closeDetailsMenuFromClick(e) {
  const details = e.currentTarget.closest('details');
  if (details) details.open = false;
}

/** Compact pipeline control: colored icon + short label (card toolbar). */
function PipeBtn({ tone, icon: Icon, className = '', children, ...rest }) {
  const toneClass = tone ? `hirelane-detail__pipeBtn--${tone}` : '';
  return (
    <Button
      type="button"
      variant="secondary"
      className={`hirelane-detail__pipeBtn ${toneClass} ${className}`.trim()}
      {...rest}
    >
      {Icon ? (
        <span className="hirelane-detail__pipeIcon" aria-hidden>
          <Icon size={14} strokeWidth={2.25} />
        </span>
      ) : null}
      <span className="hirelane-detail__pipeLabel">{children}</span>
    </Button>
  );
}

/** Compact card widget: scheduled round, when/where, calendar invite resend. */
function InterviewScheduleWidget({ interview, pipelineBusy, onSendInvite }) {
  const [linkCopied, setLinkCopied] = useState(false);
  const attendees = interviewAttendeesSummary(interview);
  const scheduledAt = new Date(interview.scheduled_at);
  const whenDate = scheduledAt.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  const whenTime = scheduledAt.toLocaleString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
  const whenFull = `${whenDate} · ${whenTime}`;
  const showInvite = interview.interview_mode === 'Online' && interview.google_calendar_event_id;
  const sentAt = interview.calendar_invite_last_sent_at;
  const inviteShort = sentAt
    ? new Date(sentAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : null;
  const inviteTitle = sentAt
    ? `Last calendar invite: ${new Date(sentAt).toLocaleString()}. Resend emails guests via Google Calendar.`
    : 'No invite email recorded yet. Send emails all guests via Google Calendar.';

  const pastDue = interviewIsPastDue(interview);
  const meetingLink = (interview.meeting_link || '').trim();
  const showJoinCta = !!meetingLink && !pastDue;
  const isOnline = interview.interview_mode === 'Online';
  const widgetLabel = pastDue
    ? `Interview past due — pending result. Was scheduled ${whenFull}.`
    : `Scheduled interview ${whenFull}`;

  const copyMeetingLink = async () => {
    if (!meetingLink) return;
    try {
      await navigator.clipboard.writeText(meetingLink);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <aside
      className={`hirelane-detail__interviewWidget${pastDue ? ' hirelane-detail__interviewWidget--pastDue' : ''}${showJoinCta ? ' hirelane-detail__interviewWidget--hasJoin' : ''}`}
      aria-label={widgetLabel}
    >
      <div className="hirelane-detail__interviewWidgetHead">
        <span className="hirelane-detail__interviewWidgetIcon" aria-hidden>
          <Calendar size={11} strokeWidth={2.25} />
        </span>
        <span className="hirelane-detail__interviewWidgetRound">{interview.display_round_label}</span>
        {pastDue ? (
          <span className="hirelane-detail__interviewWidgetPending">Pending result</span>
        ) : interview.interview_mode ? (
          <span className="hirelane-detail__interviewWidgetMode">{interview.interview_mode}</span>
        ) : null}
      </div>
      <div className="hirelane-detail__interviewWidgetMain">
        <div
          className={`hirelane-detail__interviewWidgetWhen${showJoinCta ? ' hirelane-detail__interviewWidgetWhen--hasJoin' : ''}${!showJoinCta && !meetingLink && isOnline && !pastDue ? ' hirelane-detail__interviewWidgetWhen--missingLink' : ''}`}
          role="group"
          aria-label={`Interview scheduled ${whenFull}`}
        >
          <div className="hirelane-detail__interviewWidgetWhenPrimary">
            <time dateTime={interview.scheduled_at} className="hirelane-detail__interviewWidgetWhenTime">
              {whenTime}
            </time>
            <span className="hirelane-detail__interviewWidgetWhenDate">{whenDate}</span>
          </div>
          {showJoinCta ? (
            <div className="hirelane-detail__interviewWidgetWhenActions">
              <a
                href={meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hirelane-detail__interviewWidgetJoinBtn"
              >
                <Video size={11} strokeWidth={2.25} aria-hidden />
                Join call
              </a>
              <button
                type="button"
                className="hirelane-detail__interviewWidgetJoinCopy"
                onClick={copyMeetingLink}
                title="Copy meeting link"
              >
                {linkCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          ) : null}
          {!showJoinCta && meetingLink && pastDue ? (
            <p className="hirelane-detail__interviewWidgetJoinPast">
              <a href={meetingLink} target="_blank" rel="noopener noreferrer">
                Meeting link
              </a>
              <span className="hirelane-detail__interviewWidgetJoinPastHint"> — enter outcome when ready</span>
            </p>
          ) : null}
          {!meetingLink && isOnline && !pastDue ? (
            <p
              className="hirelane-detail__interviewWidgetJoinMissing"
              title="Reschedule with Meet or paste a link, then send the calendar invite."
            >
              No meeting link yet
            </p>
          ) : null}
        </div>
        {attendees || interview.location ? (
            <dl className="hirelane-detail__interviewWidgetFacts">
              {attendees ? (
                <div className="hirelane-detail__interviewWidgetFact">
                  <dt>Guests</dt>
                  <dd title={attendees}>{attendees}</dd>
                </div>
              ) : null}
              {interview.location ? (
                <div className="hirelane-detail__interviewWidgetFact">
                  <dt>Where</dt>
                  <dd className="hirelane-detail__interviewWidgetLocation" title={interview.location}>
                    {interview.location}
                  </dd>
                </div>
              ) : null}
            </dl>
        ) : null}
      </div>
      {showInvite ? (
        <div className="hirelane-detail__interviewWidgetInviteRow">
          <span className="hirelane-detail__interviewWidgetInviteStat" title={inviteTitle}>
            <Send size={10} strokeWidth={2.5} aria-hidden />
            {sentAt ? `Sent ${inviteShort}` : 'Invite pending'}
          </span>
          <button
            type="button"
            className="hirelane-detail__interviewWidgetInviteBtn"
            disabled={pipelineBusy}
            title="Update the Google Calendar event and email all guests."
            onClick={onSendInvite}
          >
            {sentAt ? 'Resend' : 'Send invite'}
          </button>
        </div>
      ) : null}
    </aside>
  );
}

/** Compact comp / location snapshot (3×2 micro-grid). Click opens full profile slide-over. */
function CandidateFactsWidget({ candidate, onOpenProfile }) {
  const cells = buildCandidateFactCells(candidate);
  const filled = cells.filter((cell) => cell.value).length;

  return (
    <button
      type="button"
      className="hirelane-detail__factsWidget"
      onClick={onOpenProfile}
      aria-label={`Candidate profile snapshot, ${filled} of 6 fields filled. Open full profile.`}
      title="Open full profile"
    >
      <span className="hirelane-detail__factsWidgetHead">
        <span className="hirelane-detail__factsWidgetIcon" aria-hidden>
          <Briefcase size={11} strokeWidth={2.25} />
        </span>
        <span className="hirelane-detail__factsWidgetBrand">Vitals</span>
        <span className="hirelane-detail__factsWidgetFill" aria-hidden>
          {filled}/6
        </span>
      </span>
      <span className="hirelane-detail__factsGrid" role="list">
        {cells.map((cell) => {
          const display = truncateFactDisplay(cell.value);
          const empty = !display;
          return (
            <span
              key={cell.key}
              className={`hirelane-detail__factsCell${empty ? ' hirelane-detail__factsCell--empty' : ''}`}
              role="listitem"
              title={empty ? `${cell.title}: not set` : `${cell.title}: ${cell.value}`}
            >
              <span className="hirelane-detail__factsAbbr">{cell.abbr}</span>
              <span className="hirelane-detail__factsVal">{empty ? '—' : display}</span>
            </span>
          );
        })}
      </span>
    </button>
  );
}

function CandidateProfileDetails({ candidate, vendors, hasResume, onViewResume, onEdit }) {
  const rows = buildCandidateProfileRows(candidate, vendors);

  return (
    <div className="hirelane-detail__profileBody">
      {rows.length === 0 ? (
        <p className="hirelane-detail__profileEmpty">No extra details on file yet.</p>
      ) : (
        <div className="hirelane-detail__profileGrid" role="list">
          {rows.map((row) => (
            <div key={row.key} className="hirelane-detail__profileField" role="listitem">
              <div className="hirelane-detail__profileLabel">{row.label}</div>
              <div
                className={`hirelane-detail__profileValue${row.multiline ? ' hirelane-detail__profileValue--multiline' : ''}`}
              >
                {row.href ? (
                  <a
                    href={row.href}
                    className="hirelane-detail__textLink"
                    target={row.external ? '_blank' : undefined}
                    rel={row.external ? 'noopener noreferrer' : undefined}
                  >
                    {row.value}
                  </a>
                ) : (
                  row.value
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="hirelane-detail__profileActions">
        {hasResume ? (
          <button type="button" className="hirelane-detail__profileActionBtn" onClick={onViewResume}>
            View résumé
          </button>
        ) : null}
        <button type="button" className="hirelane-detail__profileActionBtn" onClick={onEdit}>
          Edit details…
        </button>
      </div>
    </div>
  );
}

function CandidateMoreActions({
  isRejectedView,
  onPipelineStage,
  onOpenScheduleInterview,
  onOpenMarkSelected,
  hasEvaluation,
  evalExpanded,
  onToggleEval,
  onDelete,
  hasResume,
  onViewResume,
  onViewProfile,
  onEditDetails
}) {
  return (
    <details className="hirelane-detail__moreMenu">
      <summary
        className="hirelane-detail__moreSummary"
        title="Shortlist, interview, reject, restore, Cony review"
      >
        More
      </summary>
      <div className="hirelane-detail__morePanel" role="menu">
        {hasResume && (
          <>
            <div className="hirelane-detail__moreGroupLabel">Profile</div>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                closeDetailsMenuFromClick(e);
                onViewResume();
              }}
            >
              View résumé
            </button>
          </>
        )}
        {isRejectedView ? (
          <>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                onPipelineStage('New');
                closeDetailsMenuFromClick(e);
              }}
            >
              Restore to New
            </button>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                onPipelineStage('Shortlisted');
                closeDetailsMenuFromClick(e);
              }}
            >
              Move to Shortlisted
            </button>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                onPipelineStage('Cony Reviewed');
                closeDetailsMenuFromClick(e);
              }}
            >
              Mark as Cony reviewed
            </button>
          </>
        ) : (
          <>
            <div className="hirelane-detail__moreGroupLabel">Pipeline</div>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                onPipelineStage('Shortlisted');
                closeDetailsMenuFromClick(e);
              }}
            >
              Shortlist
            </button>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                closeDetailsMenuFromClick(e);
                onOpenScheduleInterview?.();
              }}
            >
              Schedule interview…
            </button>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                closeDetailsMenuFromClick(e);
                onOpenMarkSelected?.();
              }}
            >
              Mark selected (hired)…
            </button>
            <button
              type="button"
              className="hirelane-detail__moreItem hirelane-detail__moreItem--danger"
              role="menuitem"
              onClick={(e) => {
                onPipelineStage('Rejected');
                closeDetailsMenuFromClick(e);
              }}
            >
              Reject
            </button>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                onPipelineStage('New');
                closeDetailsMenuFromClick(e);
              }}
            >
              Reset to New
            </button>
          </>
        )}
        {hasEvaluation && (
          <>
            <div className="hirelane-detail__moreGroupLabel">Cony review</div>
            <button
              type="button"
              className="hirelane-detail__moreItem"
              role="menuitem"
              onClick={(e) => {
                onToggleEval();
                closeDetailsMenuFromClick(e);
              }}
            >
              {evalExpanded ? 'Collapse full Cony review' : 'Expand full Cony review'}
            </button>
          </>
        )}
        <div className="hirelane-detail__moreDivider" />
        <div className="hirelane-detail__moreGroupLabel">Record</div>
        <button
          type="button"
          className="hirelane-detail__moreItem"
          role="menuitem"
          onClick={(e) => {
            closeDetailsMenuFromClick(e);
            onViewProfile?.();
          }}
        >
          View profile
        </button>
        <button
          type="button"
          className="hirelane-detail__moreItem"
          role="menuitem"
          onClick={(e) => {
            closeDetailsMenuFromClick(e);
            onEditDetails();
          }}
        >
          Edit details…
        </button>
        <button
          type="button"
          className="hirelane-detail__moreItem hirelane-detail__moreItem--danger"
          role="menuitem"
          onClick={(e) => {
            closeDetailsMenuFromClick(e);
            onDelete();
          }}
        >
          Delete candidate…
        </button>
      </div>
    </details>
  );
}

function CollapsibleEvaluation({ evaluation, expanded, onToggle, isEvaluating }) {
  const raw =
    evaluation && evaluation.raw_ai_response && typeof evaluation.raw_ai_response === 'object'
      ? evaluation.raw_ai_response
      : {};

  if (!evaluation) {
    return (
      <aside className="hirelane-detail__conyWidget" aria-label="Cony review">
        <div className="hirelane-detail__conyWidgetHead">
          <span className="hirelane-detail__conyWidgetIcon" aria-hidden>
            <Zap size={11} strokeWidth={2.5} />
          </span>
          <span className="hirelane-detail__conyWidgetBrand">Cony</span>
        </div>
        <div className="hirelane-detail__conyWidgetBadges">
          <span
            className={`hirelane-detail__conyPill hirelane-detail__conyPill--${isEvaluating ? 'loading' : 'muted'}`}
          >
            {isEvaluating ? 'Reviewing…' : 'No review yet'}
          </span>
        </div>
      </aside>
    );
  }

  const v2 =
    evaluation.recommended_decision ||
    evaluation.jd_understanding ||
    evaluation.fit_breakdown ||
    evaluation.core_role_execution_risk ||
    evaluation.fit_for_advertised_role ||
    evaluation.alternate_role_fit ||
    evaluation.critical_missing_requirements ||
    (evaluation.raw_ai_response && typeof evaluation.raw_ai_response === 'object' && evaluation.raw_ai_response.recommended_decision);

  const matchDisplay =
    evaluation.overall_match_score != null && evaluation.overall_match_score !== ''
      ? evaluation.overall_match_score
      : evaluation.match_score;

  const decision = evaluation.recommended_decision || raw.recommended_decision || evaluation.eligibility || '';
  const confidence = evaluation.decision_confidence || raw.decision_confidence || '';
  const riskLevel =
    (evaluation.core_role_execution_risk && evaluation.core_role_execution_risk.risk_level) ||
    (raw.core_role_execution_risk && raw.core_role_execution_risk.risk_level) ||
    '';
  const riskAssessment =
    (evaluation.core_role_execution_risk && evaluation.core_role_execution_risk.assessment) ||
    (raw.core_role_execution_risk && raw.core_role_execution_risk.assessment) ||
    '';
  const collapsedSummary = conyCollapsedSummary(evaluation);

  return (
    <>
      <aside className="hirelane-detail__conyWidget" aria-label="Cony review">
        <div className="hirelane-detail__conyWidgetHead">
          <span className="hirelane-detail__conyWidgetIcon" aria-hidden>
            <Zap size={11} strokeWidth={2.5} />
          </span>
          <span className="hirelane-detail__conyWidgetBrand">Cony</span>
          <button
            type="button"
            className="hirelane-detail__conyWidgetToggle"
            onClick={onToggle}
            aria-expanded={expanded}
          >
            {expanded ? 'Hide' : 'Details'}
          </button>
        </div>
        <div className="hirelane-detail__conyWidgetBadges">
          {matchDisplay != null && matchDisplay !== '' ? (
            <span className="hirelane-detail__conyMatchChip" title="Match score">
              <span className="hirelane-detail__conyMatchNum">{matchDisplay}</span>
              <span className="hirelane-detail__conyMatchLbl">Match</span>
            </span>
          ) : null}
          {confidence ? (
            <span className="hirelane-detail__conyPill hirelane-detail__conyPill--neutral">
              <span className="hirelane-detail__conyPillPref">Confidence</span> {confidence}
            </span>
          ) : null}
          {riskLevel ? (
            <span
              className="hirelane-detail__conyPill hirelane-detail__conyPill--risk"
              title={riskAssessment || undefined}
            >
              <span className="hirelane-detail__conyPillPref">Risk</span> {riskLevel}
            </span>
          ) : null}
          {decision ? (
            <span className="hirelane-detail__conyPill hirelane-detail__conyPill--decision" title="Recommended fit">
              {decision}
            </span>
          ) : null}
          {isEvaluating ? (
            <span className="hirelane-detail__conyPill hirelane-detail__conyPill--loading">Reviewing…</span>
          ) : null}
        </div>
        {!expanded && collapsedSummary ? (
          <p className="hirelane-detail__conyWidgetPreview" title={collapsedSummary}>
            {collapsedSummary}
          </p>
        ) : null}
      </aside>
      {expanded ? (
        <div className="hirelane-detail__conyWidgetExpand">
          {v2 ? (
            <CandidateEvaluationPanel
              evaluation={evaluation}
              legacyDetail={<EvaluationReviewDetail evaluation={evaluation} />}
            />
          ) : (
            <EvaluationReviewDetail evaluation={evaluation} />
          )}
        </div>
      ) : null}
    </>
  );
}

export default function HirelaneJobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  /** @type {Record<string, { record_gaps: number; profile_slots_total: number; profile_completeness_pct: number; custom_prompts_open: number }>} */
  const [clarifStatsByCandidateId, setClarifStatsByCandidateId] = useState({});

  const [jobDraft, setJobDraft] = useState({ final_jd: '', key_skills: '', responsibilities: '' });
  const [savingJob, setSavingJob] = useState(false);
  const lastSyncedJobRouteRef = useRef(null);
  const [jobBriefExpanded, setJobBriefExpanded] = useState(false);
  const [jobBriefEditing, setJobBriefEditing] = useState(false);

  const [manual, setManual] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    notes: '',
    current_ctc: '',
    expected_ctc: '',
    notice_period: '',
    notice_period_negotiable: false,
    currently_on_notice: false,
    current_location: '',
    willing_to_relocate: false,
    work_mode_preference: '',
    linkedin_url: '',
    github_url: '',
    total_experience: '',
    relevant_experience: ''
  });
  const [manualResumeFile, setManualResumeFile] = useState(null);
  const [savingManual, setSavingManual] = useState(false);
  const [manualShowMore, setManualShowMore] = useState(false);

  const [resumeFile, setResumeFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftResumeMeta, setDraftResumeMeta] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const [evaluatingId, setEvaluatingId] = useState(null);
  const [pipelineBusyId, setPipelineBusyId] = useState(null);
  const [editingCandidateId, setEditingCandidateId] = useState(null);
  const editingCandidateIdRef = useRef(null);
  const beginEditCandidateRef = useRef(() => {});
  const [candidateDrafts, setCandidateDrafts] = useState({});
  const [savingCandidateId, setSavingCandidateId] = useState(null);
  const [workflowTab, setWorkflowTab] = useState(TAB_IDS.review);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [outcomeModal, setOutcomeModal] = useState(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [expandedEval, setExpandedEval] = useState({});
  const [profilePanelCandidate, setProfilePanelCandidate] = useState(null);
  const [addCandidateModalOpen, setAddCandidateModalOpen] = useState(false);
  const [addCandidateMode, setAddCandidateMode] = useState('manual'); // 'manual' | 'resume'

  const [vendors, setVendors] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [newCandProfileSource, setNewCandProfileSource] = useState('direct');
  const [newCandVendorId, setNewCandVendorId] = useState('');
  const [newCandReferrerName, setNewCandReferrerName] = useState('');

  const [statusDecisionModal, setStatusDecisionModal] = useState(null);
  const [decisionForm, setDecisionForm] = useState({
    reason: '',
    comments: '',
    recommended_next_step: ''
  });
  const [pageToast, setPageToast] = useState(null);
  /** Bumped when interviews or status history change so CandidateInterviewTimeline refetches. */
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const bumpTimelineRefresh = () => setTimelineRefreshKey((k) => k + 1);

  useEffect(() => {
    if (!pageToast) return undefined;
    const t = setTimeout(() => setPageToast(null), 4500);
    return () => clearTimeout(t);
  }, [pageToast]);

  useEffect(() => {
    editingCandidateIdRef.current = editingCandidateId;
  }, [editingCandidateId]);

  const refresh = useCallback(async () => {
    const [j, c] = await Promise.all([
      hirelaneApi.getJob(jobId),
      hirelaneApi.listCandidates(jobId, { include: 'latest_evaluation,interview_summary' })
    ]);
    setJob(j);
    const list = Array.isArray(c) ? c : [];
    setCandidates(list);
    return { job: j, candidates: list };
  }, [jobId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        await refresh();
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load job');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, refresh]);

  useEffect(() => {
    setAddCandidateModalOpen(false);
  }, [jobId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, intr] = await Promise.all([hirelaneApi.listVendors(), hirelaneApi.listInterviewers()]);
        if (!cancelled) {
          setVendors(Array.isArray(v) ? v : []);
          setInterviewers(Array.isArray(intr) ? intr : []);
        }
      } catch {
        if (!cancelled) {
          setVendors([]);
          setInterviewers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (!addCandidateModalOpen) return;
    setNewCandProfileSource('direct');
    setNewCandVendorId('');
    setNewCandReferrerName('');
  }, [addCandidateModalOpen]);

  useEffect(() => {
    setJobBriefExpanded(false);
    setJobBriefEditing(false);
  }, [jobId]);

  const jobBriefDirty = useMemo(() => {
    if (!job) return false;
    return (
      (jobDraft.final_jd ?? '') !== (job.final_jd ?? '') ||
      (jobDraft.key_skills ?? '') !== (job.key_skills ?? '') ||
      (jobDraft.responsibilities ?? '') !== (job.responsibilities ?? '')
    );
  }, [job, jobDraft]);

  useEffect(() => {
    if (!job || job.id !== jobId) return;
    if (lastSyncedJobRouteRef.current !== jobId) {
      lastSyncedJobRouteRef.current = jobId;
      setJobDraft({
        final_jd: job.final_jd ?? '',
        key_skills: job.key_skills ?? '',
        responsibilities: job.responsibilities ?? ''
      });
    }
  }, [job, jobId]);

  useEffect(() => {
    if (!job || job.id !== jobId) return;
    if (!jobBriefDirty) {
      setJobDraft({
        final_jd: job.final_jd ?? '',
        key_skills: job.key_skills ?? '',
        responsibilities: job.responsibilities ?? ''
      });
    }
  }, [job, jobId, jobBriefDirty]);

  const tabCounts = useMemo(
    () => ({
      review: candidatesForTab(candidates, TAB_IDS.review).length,
      shortlisted: candidatesForTab(candidates, TAB_IDS.shortlisted).length,
      interview: candidatesForTab(candidates, TAB_IDS.interview).length,
      selected: candidatesForTab(candidates, TAB_IDS.selected).length,
      rejected: candidatesForTab(candidates, TAB_IDS.rejected).length
    }),
    [candidates]
  );

  const shownList = useMemo(
    () => candidatesForTabSorted(candidates, workflowTab),
    [candidates, workflowTab]
  );

  useEffect(() => {
    let cancelled = false;
    const ids = candidates.map((c) => c.id);
    if (!ids.length) {
      setClarifStatsByCandidateId({});
      return undefined;
    }
    (async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const s = await hirelaneClarificationApi.candidateStats(id);
            return [id, s];
          } catch {
            return [id, null];
          }
        })
      );
      if (cancelled) return;
      const next = {};
      for (const [id, s] of entries) {
        if (
          s &&
          typeof s.record_gaps === 'number' &&
          typeof s.profile_slots_total === 'number' &&
          typeof s.profile_completeness_pct === 'number' &&
          typeof s.custom_prompts_open === 'number'
        ) {
          next[id] = s;
        }
      }
      setClarifStatsByCandidateId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [candidates]);

  const resetJobDraft = () => {
    if (!job) return;
    setJobDraft({
      final_jd: job.final_jd ?? '',
      key_skills: job.key_skills ?? '',
      responsibilities: job.responsibilities ?? ''
    });
  };

  const saveJobBrief = async () => {
    try {
      setSavingJob(true);
      setError(null);
      const updated = await hirelaneApi.updateJob(jobId, {
        final_jd: jobDraft.final_jd || null,
        key_skills: jobDraft.key_skills || null,
        responsibilities: jobDraft.responsibilities || null
      });
      setJob(updated);
      setJobDraft({
        final_jd: updated.final_jd ?? '',
        key_skills: updated.key_skills ?? '',
        responsibilities: updated.responsibilities ?? ''
      });
      setJobBriefEditing(false);
    } catch (e) {
      setError(e?.message || 'Failed to save job');
    } finally {
      setSavingJob(false);
    }
  };

  const collapseJobBrief = () => {
    if (jobBriefDirty) {
      const ok = window.confirm('Discard unsaved changes to the job brief?');
      if (!ok) return;
      resetJobDraft();
    }
    setJobBriefExpanded(false);
    setJobBriefEditing(false);
  };

  const expandJobBriefReadOnly = () => {
    setJobBriefExpanded(true);
    setJobBriefEditing(false);
  };

  const startJobBriefEdit = () => {
    setJobBriefExpanded(true);
    setJobBriefEditing(true);
  };

  const exitJobBriefEdit = () => {
    if (jobBriefDirty) {
      const ok = window.confirm('Discard unsaved edits and return to view mode?');
      if (!ok) return;
      resetJobDraft();
    }
    setJobBriefEditing(false);
  };

  const addManual = async () => {
    try {
      setSavingManual(true);
      setError(null);
      if (!manual.name.trim()) return;
      if (newCandProfileSource === 'vendor' && !newCandVendorId) {
        setError('Select a vendor, or choose Direct / Referral.');
        return;
      }
      if (newCandProfileSource === 'referral' && !newCandReferrerName.trim()) {
        setError('Enter the referrer name.');
        return;
      }
      const profilePayload = {
        profile_source: newCandProfileSource,
        hirelane_vendor_id: newCandProfileSource === 'vendor' ? newCandVendorId : null,
        referrer_name: newCandProfileSource === 'referral' ? newCandReferrerName.trim() : null
      };
      await hirelaneApi.createCandidate(
        jobId,
        { ...manual, name: manual.name.trim(), source_type: 'manual', ...profilePayload },
        manualResumeFile || undefined
      );
      setManual({
        name: '',
        email: '',
        phone: '',
        skills: '',
        notes: '',
        current_ctc: '',
        expected_ctc: '',
        notice_period: '',
        notice_period_negotiable: false,
        currently_on_notice: false,
        current_location: '',
        willing_to_relocate: false,
        work_mode_preference: '',
        linkedin_url: '',
        github_url: '',
        total_experience: '',
        relevant_experience: ''
      });
      setManualResumeFile(null);
      setManualShowMore(false);
      setAddCandidateModalOpen(false);
      await refresh();
      bumpTimelineRefresh();
    } catch (e) {
      setError(e?.message || 'Failed to add candidate');
    } finally {
      setSavingManual(false);
    }
  };

  const extract = async () => {
    if (!resumeFile) return;
    try {
      setExtracting(true);
      setError(null);
      const result = await hirelaneApi.parseResumeAndExtractDraft(jobId, resumeFile);
      setDraft(result?.extractedCandidateDraft || null);
      setDraftResumeMeta(result?.uploadedFile || null);
      if (!result?.extractedCandidateDraft) setError('No draft returned from extraction');
    } catch (e) {
      setError(e?.message || 'Failed to extract');
    } finally {
      setExtracting(false);
    }
  };

  const saveDraft = async () => {
    if (!draft?.name?.trim()) return;
    if (newCandProfileSource === 'vendor' && !newCandVendorId) {
      setError('Select a vendor, or choose Direct / Referral.');
      return;
    }
    if (newCandProfileSource === 'referral' && !newCandReferrerName.trim()) {
      setError('Enter the referrer name.');
      return;
    }
    try {
      setSavingDraft(true);
      setError(null);
      await hirelaneApi.createCandidateFromExtraction(jobId, {
        draft,
        resume_file_path: draftResumeMeta?.file_path || null,
        resume_text: null,
        profile_source: newCandProfileSource,
        hirelane_vendor_id: newCandProfileSource === 'vendor' ? newCandVendorId : null,
        referrer_name: newCandProfileSource === 'referral' ? newCandReferrerName.trim() : null
      });
      setDraft(null);
      setDraftResumeMeta(null);
      setResumeFile(null);
      setAddCandidateModalOpen(false);
      await refresh();
      bumpTimelineRefresh();
    } catch (e) {
      setError(e?.message || 'Failed to save candidate');
    } finally {
      setSavingDraft(false);
    }
  };

  const evaluate = async (candidateId) => {
    try {
      setEvaluatingId(candidateId);
      setError(null);
      await hirelaneApi.evaluateCandidate(candidateId);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Cony review could not be completed');
    } finally {
      setEvaluatingId(null);
    }
  };

  const routeTabAfterStatus = (status) => {
    if (status === 'Rejected') setWorkflowTab(TAB_IDS.rejected);
    else if (status === 'Interview Scheduled') setWorkflowTab(TAB_IDS.interview);
    else if (['Shortlisted', 'Round Cleared', 'Hold Pipeline'].includes(status)) setWorkflowTab(TAB_IDS.shortlisted);
    else if (status === 'Selected') setWorkflowTab(TAB_IDS.selected);
    else if (
      ['New', 'Cony Reviewed', 'Under Review', 'Needs Clarification', 'Hold Profile'].includes(status)
    )
      setWorkflowTab(TAB_IDS.review);
  };

  const updateStageSimple = async (candidateId, status, extra = {}) => {
    try {
      setPipelineBusyId(candidateId);
      setError(null);
      await hirelaneApi.updateCandidateStatus(candidateId, { status, ...extra });
      setCandidates((prev) => prev.map((c) => (c.id === candidateId ? { ...c, status } : c)));
      routeTabAfterStatus(status);
      bumpTimelineRefresh();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to update stage';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setPipelineBusyId(null);
    }
  };

  const openStatusDecisionModal = (candidate, mode) => {
    setStatusDecisionModal({
      mode,
      candidateId: candidate.id,
      candidateName: (candidate.name && candidate.name.trim()) || 'this candidate',
      previousStatus: candidate.status
    });
    setDecisionForm({ reason: '', comments: '', recommended_next_step: '' });
  };

  const closeStatusDecisionModal = () => setStatusDecisionModal(null);

  const applyPipelineStage = (candidate, nextStatus) => {
    if (nextStatus === 'Rejected' || nextStatus === 'Shortlisted') {
      openStatusDecisionModal(candidate, nextStatus === 'Rejected' ? 'reject' : 'shortlist');
      return;
    }
    if (nextStatus === 'Selected') {
      openStatusDecisionModal(candidate, 'select');
      return;
    }
    if (nextStatus === 'Hold Pipeline') {
      openStatusDecisionModal(candidate, 'hold_pipeline');
      return;
    }
    updateStageSimple(candidate.id, nextStatus);
  };

  const confirmStatusDecision = async () => {
    if (!statusDecisionModal) return;
    const { mode, candidateId } = statusDecisionModal;
    const reason = (decisionForm.reason || '').trim();
    const comments = (decisionForm.comments || '').trim();
    const recommended_next_step = (decisionForm.recommended_next_step || '').trim();
    if (mode === 'reject' || mode === 'shortlist') {
      if (!reason || !comments) return;
      if (mode === 'shortlist' && !recommended_next_step) return;
    }
    if ((mode === 'select' || mode === 'hold_pipeline') && !comments) return;

    try {
      setPipelineBusyId(candidateId);
      setError(null);
      if (mode === 'reject') {
        await hirelaneApi.updateCandidateStatus(candidateId, {
          status: 'Rejected',
          reason,
          comments
        });
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, status: 'Rejected' } : c))
        );
        setWorkflowTab(TAB_IDS.rejected);
        setPageToast({ type: 'success', message: 'Candidate rejected.' });
      } else if (mode === 'shortlist') {
        await hirelaneApi.updateCandidateStatus(candidateId, {
          status: 'Shortlisted',
          reason,
          comments,
          recommended_next_step
        });
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, status: 'Shortlisted' } : c))
        );
        setWorkflowTab(TAB_IDS.shortlisted);
        setPageToast({ type: 'success', message: 'Candidate shortlisted.' });
      } else if (mode === 'select') {
        await hirelaneApi.updateCandidateStatus(candidateId, {
          status: 'Selected',
          comments
        });
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, status: 'Selected' } : c))
        );
        setWorkflowTab(TAB_IDS.selected);
        setPageToast({ type: 'success', message: 'Candidate marked selected.' });
      } else if (mode === 'hold_pipeline') {
        await hirelaneApi.updateCandidateStatus(candidateId, {
          status: 'Hold Pipeline',
          comments
        });
        setCandidates((prev) =>
          prev.map((c) => (c.id === candidateId ? { ...c, status: 'Hold Pipeline' } : c))
        );
        setWorkflowTab(TAB_IDS.shortlisted);
        setPageToast({ type: 'success', message: 'Candidate placed on hold.' });
      }
      setStatusDecisionModal(null);
      await refresh();
      bumpTimelineRefresh();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to update status';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setPipelineBusyId(null);
    }
  };

  const decisionFormValid =
    !!statusDecisionModal &&
    (statusDecisionModal.mode === 'select' || statusDecisionModal.mode === 'hold_pipeline'
      ? !!(decisionForm.comments || '').trim()
      : statusDecisionModal.mode === 'reject' || statusDecisionModal.mode === 'shortlist'
        ? !!(decisionForm.reason || '').trim() &&
          !!(decisionForm.comments || '').trim() &&
          (statusDecisionModal.mode === 'reject' || !!(decisionForm.recommended_next_step || '').trim())
        : false);

  const submitScheduleInterview = async (payload, forceDuplicate = false) => {
    if (!scheduleModal?.candidate) return;
    const id = scheduleModal.candidate.id;
    try {
      setScheduleSubmitting(true);
      setError(null);
      const schedData = await hirelaneApi.scheduleInterview(id, {
        ...payload,
        force: forceDuplicate
      });
      setScheduleModal(null);
      setWorkflowTab(TAB_IDS.interview);
      await refresh();
      bumpTimelineRefresh();
      if (schedData.calendarWarning) {
        setPageToast({ type: 'warning', message: schedData.calendarWarning });
      } else {
        setPageToast({ type: 'success', message: 'Interview scheduled.' });
      }
    } catch (e) {
      if (e?.response?.status === 409 && !forceDuplicate) {
        const ok = window.confirm(
          `${e.response?.data?.error || 'Another interview is already scheduled.'} Schedule another anyway?`
        );
        if (ok) await submitScheduleInterview(payload, true);
        return;
      }
      const msg = e?.response?.data?.error || e?.message || 'Failed to schedule';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const submitRescheduleInterview = async (payload) => {
    if (!rescheduleModal?.candidate || !rescheduleModal?.interview) return;
    try {
      setScheduleSubmitting(true);
      setError(null);
      const patchData = await hirelaneApi.patchInterview(
        rescheduleModal.candidate.id,
        rescheduleModal.interview.id,
        payload
      );
      setRescheduleModal(null);
      await refresh();
      bumpTimelineRefresh();
      if (patchData.calendarWarning) {
        setPageToast({ type: 'warning', message: patchData.calendarWarning });
      } else {
        setPageToast({ type: 'success', message: 'Interview updated.' });
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to update interview';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const submitInterviewOutcome = async (payload) => {
    if (!outcomeModal?.candidate || !outcomeModal?.interview) return;
    try {
      setScheduleSubmitting(true);
      setError(null);
      await hirelaneApi.recordInterviewOutcome(outcomeModal.candidate.id, outcomeModal.interview.id, payload);
      setOutcomeModal(null);
      await refresh();
      bumpTimelineRefresh();
      routeTabAfterStatus(
        payload.outcome === 'Rejected'
          ? 'Rejected'
          : payload.outcome === 'Final Selected'
            ? 'Selected'
            : payload.outcome === 'Round Cleared'
              ? 'Round Cleared'
              : 'Hold Pipeline'
      );
      setPageToast({ type: 'success', message: 'Outcome saved.' });
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to save outcome';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const deleteCandidate = async (candidateId, candidateName) => {
    const label = candidateName?.trim() || 'this candidate';
    const ok = window.confirm(
      `Permanently delete ${label}? This removes the candidate and their Cony reviews. This cannot be undone.`
    );
    if (!ok) return;
    try {
      setError(null);
      await hirelaneApi.deleteCandidate(candidateId);
      setExpandedEval((prev) => {
        const next = { ...prev };
        delete next[candidateId];
        return next;
      });
      await refresh();
      bumpTimelineRefresh();
    } catch (e) {
      setError(e?.message || 'Failed to delete candidate');
    }
  };

  const viewResume = async (candidateId) => {
    try {
      setError(null);
      const res = await hirelaneApi.getCandidateResumeUrl(candidateId);
      const url = res?.url;
      if (!url) throw new Error('No resume URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e?.message || 'Failed to open resume');
    }
  };

  const beginEditCandidate = (c) => {
    const inferredProfile =
      c.profile_source && ['vendor', 'referral', 'direct'].includes(c.profile_source)
        ? c.profile_source
        : c.hirelane_vendor_id
          ? 'vendor'
          : c.referrer_name
            ? 'referral'
            : 'direct';
    setProfilePanelCandidate(null);
    setEditingCandidateId(c.id);
    setCandidateDrafts((prev) => ({
      ...prev,
      [c.id]: {
        email: c.email ?? '',
        phone: c.phone ?? '',
        current_company: c.current_company ?? '',
        current_designation: c.current_designation ?? '',
        total_experience: c.total_experience ?? '',
        relevant_experience: c.relevant_experience ?? '',
        current_location: c.current_location ?? '',
        notice_period: c.notice_period ?? '',
        notice_period_negotiable: c.notice_period_negotiable ?? false,
        currently_on_notice: c.currently_on_notice ?? false,
        expected_ctc: c.expected_ctc ?? '',
        current_ctc: c.current_ctc ?? '',
        willing_to_relocate: c.willing_to_relocate ?? false,
        work_mode_preference: c.work_mode_preference ?? '',
        linkedin_url: c.linkedin_url ?? '',
        github_url: c.github_url ?? '',
        skills: c.skills ?? '',
        notes: c.notes ?? '',
        profile_source: inferredProfile,
        hirelane_vendor_id: c.hirelane_vendor_id ?? '',
        referrer_name: c.referrer_name ?? ''
      }
    }));
    requestAnimationFrame(() => {
      document.getElementById(`hirelane-cand-card-${c.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  beginEditCandidateRef.current = beginEditCandidate;

  const handleClarificationQuestionsUpdated = useCallback(
    async (clarCandidateId) => {
      try {
        const { candidates: list } = await refresh();
        const fresh = list?.find((x) => x.id === clarCandidateId);
        if (fresh && editingCandidateIdRef.current === clarCandidateId) {
          beginEditCandidateRef.current(fresh);
        }
      } catch {
        /* refresh failed — ignore; user can reload */
      }
    },
    [refresh]
  );

  const cancelEditCandidate = () => {
    setEditingCandidateId(null);
  };

  const saveCandidateDetails = async (candidateId) => {
    const draft = candidateDrafts[candidateId];
    if (!draft) return;
    if (draft.profile_source === 'vendor' && !draft.hirelane_vendor_id) {
      setError('Select a vendor, or choose Direct / Referral for profile source.');
      return;
    }
    if (draft.profile_source === 'referral' && !String(draft.referrer_name || '').trim()) {
      setError('Enter the referrer name for a referral profile.');
      return;
    }
    try {
      setSavingCandidateId(candidateId);
      setError(null);
      const payload = {
        ...draft,
        profile_source: draft.profile_source,
        hirelane_vendor_id:
          draft.profile_source === 'vendor' && draft.hirelane_vendor_id ? draft.hirelane_vendor_id : null,
        referrer_name:
          draft.profile_source === 'referral' ? String(draft.referrer_name || '').trim() || null : null
      };
      await hirelaneApi.updateCandidate(candidateId, payload);
      setEditingCandidateId(null);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Failed to update candidate');
    } finally {
      setSavingCandidateId(null);
    }
  };

  const toggleEvalExpanded = (id) => {
    setExpandedEval((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openProfilePanel = (c) => setProfilePanelCandidate(c);
  const closeProfilePanel = () => setProfilePanelCandidate(null);

  useEffect(() => {
    if (!profilePanelCandidate) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeProfilePanel();
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [profilePanelCandidate]);

  const cancelScheduledInterview = async (cand, inv) => {
    if (!window.confirm(`Cancel ${inv.display_round_label}?`)) return;
    try {
      setPipelineBusyId(cand.id);
      setError(null);
      await hirelaneApi.patchInterview(cand.id, inv.id, { status: 'Cancelled' });
      await refresh();
      bumpTimelineRefresh();
      setPageToast({ type: 'success', message: 'Interview cancelled.' });
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to cancel';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setPipelineBusyId(null);
    }
  };

  const sendOrResendCalendarInvites = async (cand, inv) => {
    try {
      setPipelineBusyId(cand.id);
      setError(null);
      const data = await hirelaneApi.sendCalendarInterviewInvites(cand.id, inv.id);
      if (data?.ok === false) {
        setPageToast({
          type: 'warning',
          message: data.calendarWarning || data.error || 'Could not send calendar invitations.'
        });
        return;
      }
      await refresh();
      const kind = data?.wasResend ? 'Resend' : 'First send from Hirelane';
      if (data?.calendarWarning) {
        setPageToast({
          type: 'warning',
          message: `${data.calendarWarning} (${kind}.)`
        });
      } else {
        setPageToast({
          type: 'success',
          message: data?.wasResend
            ? 'Google Calendar was updated and invitation emails were resent to guests on this event.'
            : 'Google Calendar was updated and invitation emails were sent to guests on this event (first send from Hirelane).'
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to send calendar invitations';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setPipelineBusyId(null);
    }
  };

  const reopenCandidateFlow = async (cand) => {
    const comments = window.prompt('Comments (required) for reopening this candidate:');
    if (!comments || !comments.trim()) return;
    const toShortlist = window.confirm(
      'OK = reopen to Shortlisted. Cancel = reopen to Under Review.'
    );
    try {
      setPipelineBusyId(cand.id);
      setError(null);
      await hirelaneApi.reopenCandidate(cand.id, {
        target_status: toShortlist ? 'Shortlisted' : 'Under Review',
        comments: comments.trim()
      });
      await refresh();
      bumpTimelineRefresh();
      setWorkflowTab(toShortlist ? TAB_IDS.shortlisted : TAB_IDS.review);
      setPageToast({ type: 'success', message: 'Candidate reopened.' });
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to reopen';
      setError(msg);
      setPageToast({ type: 'error', message: msg });
    } finally {
      setPipelineBusyId(null);
    }
  };

  const renderCandidateCard = (c) => {
    const accent = STATUS_ACCENTS[c.status] || STATUS_ACCENTS.New;
    const evalExpanded = !!expandedEval[c.id];
    const pipelineBusy = pipelineBusyId === c.id;
    const summ = c.interview_summary || {};
    const activeInv = summ.active_scheduled_interview;

    const canShortlistFromReview =
      workflowTab === TAB_IDS.review &&
      !['Shortlisted', 'Round Cleared', 'Hold Pipeline', 'Interview Scheduled', 'Selected', 'Rejected'].includes(
        c.status
      );
    const showRejectOnReview = workflowTab === TAB_IDS.review && c.status !== 'Rejected';
    const showShortlistOnRejected = workflowTab === TAB_IDS.rejected;

    const shortlistedActions =
      workflowTab === TAB_IDS.shortlisted && !['Selected', 'Rejected'].includes(c.status);

    const interviewActions = workflowTab === TAB_IDS.interview && activeInv;

    const terminal = c.status === 'Selected' || c.status === 'Rejected';
    const metaChips = candidateCardMetaChips(c);
    const clarifSt = clarifStatsByCandidateId[c.id];
    const signalChips =
      workflowTab === TAB_IDS.shortlisted || workflowTab === TAB_IDS.interview
        ? candidateCardSignalChips(c, summ)
        : [];
    const joinChip = signalChips.find((chip) => chip.variant === 'join');
    const contextSignalChips = signalChips.filter((chip) => chip.variant !== 'join');

    return (
      <div
        key={c.id}
        id={`hirelane-cand-card-${c.id}`}
        className="hirelane-detail__card"
        style={{ borderLeftColor: accent.border }}
      >
        <div className="hirelane-detail__cardTop">
          <div className="hirelane-detail__cardIdentity">
            <div className="hirelane-detail__cardTitleRow">
              <button
                type="button"
                className="hirelane-detail__cardName hirelane-detail__cardNameBtn"
                onClick={() => openProfilePanel(c)}
                title="View full candidate profile"
              >
                {c.name}
              </button>
              <div className="hirelane-detail__cardStageGroup">
                <span
                  className="hirelane-detail__statusPill"
                  style={{ background: accent.pillBg, color: accent.pillFg }}
                >
                  {hirelanePipelineStageLabel(c.status)}
                </span>
                {joinChip && !(workflowTab === TAB_IDS.interview && activeInv) ? (
                  <a
                    href={joinChip.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hirelane-detail__signalChip hirelane-detail__signalChip--join"
                  >
                    <Video size={11} strokeWidth={2.5} aria-hidden />
                    {joinChip.label}
                  </a>
                ) : null}
              </div>
            </div>
            {metaChips.length > 0 ? (
              <div className="hirelane-detail__cardMeta" aria-label="Company, contact, and profile">
                {metaChips.map((chip) => (
                  <span
                    key={chip.key}
                    className={`hirelane-detail__metaChip${chip.key === 'company' ? ' hirelane-detail__metaChip--company' : ''}`}
                    title={chip.title}
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            ) : (
              <div className="hirelane-detail__cardMeta hirelane-detail__cardMeta--empty">No contact on file</div>
            )}
            {contextSignalChips.length > 0 ? (
              <div className="hirelane-detail__cardSignals" aria-label="Pipeline context">
                {contextSignalChips.map((chip) => (
                  <span key={chip.key} className="hirelane-detail__signalChip">
                    {chip.label}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <div className="hirelane-detail__cardToolbar">
            <PipeBtn
              tone={c.latest_evaluation ? 'evaluateSubtle' : 'evaluate'}
              icon={Zap}
              onClick={() => evaluate(c.id)}
              disabled={evaluatingId === c.id || pipelineBusy || terminal}
              title={
                evaluatingId === c.id
                  ? 'Cony is reviewing the candidate…'
                  : c.latest_evaluation
                    ? 'Run Cony again using the current job description and candidate profile / résumé'
                    : 'Run a Cony review against this job description and the candidate profile / résumé'
              }
            >
              {evaluatingId === c.id ? 'Reviewing' : c.latest_evaluation ? 'Re-run' : 'Cony'}
            </PipeBtn>
            {canShortlistFromReview ? (
              <PipeBtn
                tone="shortlist"
                icon={TrendingUp}
                onClick={() => openStatusDecisionModal(c, 'shortlist')}
                disabled={pipelineBusy}
                title="Move candidate to Shortlisted"
              >
                Shortlist
              </PipeBtn>
            ) : null}
            {showRejectOnReview ? (
              <PipeBtn
                tone="reject"
                icon={XCircle}
                onClick={() => openStatusDecisionModal(c, 'reject')}
                disabled={pipelineBusy}
                title="Reject this candidate (moves to Rejected tab)"
              >
                Reject
              </PipeBtn>
            ) : null}
            {showShortlistOnRejected ? (
              <PipeBtn
                tone="shortlist"
                icon={TrendingUp}
                onClick={() => openStatusDecisionModal(c, 'shortlist')}
                disabled={pipelineBusy}
                title="Move candidate back to Shortlisted"
              >
                Shortlist
              </PipeBtn>
            ) : null}
            {shortlistedActions ? (
              <>
                <PipeBtn
                  tone="schedule"
                  icon={Calendar}
                  onClick={() => setScheduleModal({ candidate: c })}
                  disabled={pipelineBusy}
                  title="Schedule an interview round"
                >
                  Schedule
                </PipeBtn>
                <PipeBtn
                  tone="select"
                  icon={Award}
                  onClick={() => openStatusDecisionModal(c, 'select')}
                  disabled={pipelineBusy}
                  title="Mark candidate as selected"
                >
                  Select
                </PipeBtn>
                <PipeBtn
                  tone="reject"
                  icon={XCircle}
                  onClick={() => openStatusDecisionModal(c, 'reject')}
                  disabled={pipelineBusy}
                  title="Reject this candidate"
                >
                  Reject
                </PipeBtn>
                <PipeBtn
                  tone="hold"
                  icon={PauseCircle}
                  onClick={() => openStatusDecisionModal(c, 'hold_pipeline')}
                  disabled={pipelineBusy}
                  title="Put candidate on pipeline hold"
                >
                  Hold
                </PipeBtn>
              </>
            ) : null}
            {interviewActions ? (
              <>
                <PipeBtn
                  tone="result"
                  icon={Clipboard}
                  onClick={() => setOutcomeModal({ candidate: c, interview: activeInv })}
                  disabled={pipelineBusy}
                  title="Record outcome for this interview round"
                >
                  Result
                </PipeBtn>
                <PipeBtn
                  tone="reschedule"
                  icon={RefreshCw}
                  onClick={() => setRescheduleModal({ candidate: c, interview: activeInv })}
                  disabled={pipelineBusy}
                  title="Reschedule this interview"
                >
                  Reschedule
                </PipeBtn>
                <PipeBtn
                  tone="cancelInv"
                  icon={XSquare}
                  onClick={() => cancelScheduledInterview(c, activeInv)}
                  disabled={pipelineBusy}
                  title="Cancel this scheduled interview"
                >
                  Cancel
                </PipeBtn>
              </>
            ) : null}
            {(workflowTab === TAB_IDS.selected || workflowTab === TAB_IDS.rejected) && terminal ? (
              <PipeBtn
                tone="reopen"
                icon={RotateCcw}
                onClick={() => reopenCandidateFlow(c)}
                disabled={pipelineBusy}
                title="Reopen this candidate with required comments"
              >
                Reopen
              </PipeBtn>
            ) : null}
            <CandidateMoreActions
              isRejectedView={workflowTab === TAB_IDS.rejected}
              onPipelineStage={(status) => applyPipelineStage(c, status)}
              onOpenScheduleInterview={() => setScheduleModal({ candidate: c })}
              onOpenMarkSelected={() => openStatusDecisionModal(c, 'select')}
              hasEvaluation={!!c.latest_evaluation}
              evalExpanded={evalExpanded}
              onToggleEval={() => toggleEvalExpanded(c.id)}
              onDelete={() => deleteCandidate(c.id, c.name)}
              hasResume={!!c.resume_file_path}
              onViewResume={() => viewResume(c.id)}
              onViewProfile={() => openProfilePanel(c)}
              onEditDetails={() => beginEditCandidate(c)}
            />
          </div>
        </div>

        <div className="hirelane-detail__cardWidgets">
          <CandidateFactsWidget candidate={c} onOpenProfile={() => openProfilePanel(c)} />
          {workflowTab === TAB_IDS.interview && activeInv ? (
            <InterviewScheduleWidget
              interview={activeInv}
              pipelineBusy={pipelineBusy}
              onSendInvite={() => sendOrResendCalendarInvites(c, activeInv)}
            />
          ) : null}
          <CollapsibleEvaluation
            evaluation={c.latest_evaluation}
            expanded={evalExpanded}
            onToggle={() => toggleEvalExpanded(c.id)}
            isEvaluating={evaluatingId === c.id}
          />
        </div>
        {editingCandidateId === c.id && (
          <div className="hirelane-detail__recordEdit">
            <div className="hirelane-job-form__stack hirelane-detail__candidateEditWrap">
              <section className="hirelane-job-form__panel">
                <p className="hirelane-job-form__hint" style={{ marginTop: 0 }}>
                  Update the candidate record. Optional fields match <strong>Add candidate → Show more</strong>.
                </p>

                <div className="hirelane-job-form__field hirelane-job-form__field--stack" style={{ marginBottom: 14 }}>
                  <span className="hirelane-job-form__label">Profile source</span>
                  <div className="hirelane-job-form__segmented" style={{ flexWrap: 'wrap', gap: 8 }}>
                    <Button
                      type="button"
                      variant={candidateDrafts[c.id]?.profile_source === 'direct' ? undefined : 'secondary'}
                      onClick={() =>
                        setCandidateDrafts((prev) => ({
                          ...prev,
                          [c.id]: {
                            ...prev[c.id],
                            profile_source: 'direct',
                            hirelane_vendor_id: '',
                            referrer_name: ''
                          }
                        }))
                      }
                    >
                      Direct
                    </Button>
                    <Button
                      type="button"
                      variant={candidateDrafts[c.id]?.profile_source === 'vendor' ? undefined : 'secondary'}
                      onClick={() =>
                        setCandidateDrafts((prev) => ({
                          ...prev,
                          [c.id]: { ...prev[c.id], profile_source: 'vendor', referrer_name: '' }
                        }))
                      }
                    >
                      Vendor
                    </Button>
                    <Button
                      type="button"
                      variant={candidateDrafts[c.id]?.profile_source === 'referral' ? undefined : 'secondary'}
                      onClick={() =>
                        setCandidateDrafts((prev) => ({
                          ...prev,
                          [c.id]: { ...prev[c.id], profile_source: 'referral', hirelane_vendor_id: '' }
                        }))
                      }
                    >
                      Referral
                    </Button>
                  </div>
                  {candidateDrafts[c.id]?.profile_source === 'vendor' ? (
                    <div className="hirelane-job-form__field hirelane-job-form__field--stack" style={{ marginTop: 10 }}>
                      <span className="hirelane-job-form__label">Vendor</span>
                      {vendors.length === 0 ? (
                        <p className="hirelane-job-form__hint">
                          Add vendors from the{' '}
                          <button
                            type="button"
                            className="hirelane-detail__textLink"
                            onClick={() => navigate('/hirelane')}
                          >
                            Hiring dashboard
                          </button>
                          .
                        </p>
                      ) : (
                        <select
                          className="hirelane-job-form__input"
                          value={candidateDrafts[c.id]?.hirelane_vendor_id || ''}
                          onChange={(e) =>
                            setCandidateDrafts((prev) => ({
                              ...prev,
                              [c.id]: { ...prev[c.id], hirelane_vendor_id: e.target.value }
                            }))
                          }
                          aria-label="Vendor"
                        >
                          <option value="">Select vendor…</option>
                          {vendors.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ) : null}
                  {candidateDrafts[c.id]?.profile_source === 'referral' ? (
                    <div className="hirelane-job-form__field hirelane-job-form__field--stack" style={{ marginTop: 10 }}>
                      <span className="hirelane-job-form__label">Referrer name</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.referrer_name ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], referrer_name: e.target.value }
                          }))
                        }
                        placeholder="Who referred this candidate?"
                        autoComplete="off"
                      />
                    </div>
                  ) : null}
                </div>

                <div className="hirelane-job-form__grid">
                  <div className="hirelane-job-form__field">
                    <label className="hirelane-job-form__label" htmlFor={`hirelane-cand-edit-${c.id}-email`}>
                      Email
                    </label>
                    <input
                      id={`hirelane-cand-edit-${c.id}-email`}
                      className="hirelane-job-form__input"
                      value={candidateDrafts[c.id]?.email ?? ''}
                      onChange={(e) =>
                        setCandidateDrafts((prev) => ({ ...prev, [c.id]: { ...prev[c.id], email: e.target.value } }))
                      }
                      placeholder="Email"
                      autoComplete="off"
                    />
                  </div>
                  <div className="hirelane-job-form__field">
                    <label className="hirelane-job-form__label" htmlFor={`hirelane-cand-edit-${c.id}-phone`}>
                      Phone
                    </label>
                    <input
                      id={`hirelane-cand-edit-${c.id}-phone`}
                      className="hirelane-job-form__input"
                      value={candidateDrafts[c.id]?.phone ?? ''}
                      onChange={(e) =>
                        setCandidateDrafts((prev) => ({ ...prev, [c.id]: { ...prev[c.id], phone: e.target.value } }))
                      }
                      placeholder="Phone"
                      autoComplete="off"
                    />
                  </div>
                  <div className="hirelane-job-form__field">
                    <label className="hirelane-job-form__label" htmlFor={`hirelane-cand-edit-${c.id}-company`}>
                      Current company
                    </label>
                    <input
                      id={`hirelane-cand-edit-${c.id}-company`}
                      className="hirelane-job-form__input"
                      value={candidateDrafts[c.id]?.current_company ?? ''}
                      onChange={(e) =>
                        setCandidateDrafts((prev) => ({
                          ...prev,
                          [c.id]: { ...prev[c.id], current_company: e.target.value }
                        }))
                      }
                      placeholder="Company"
                      autoComplete="organization"
                    />
                  </div>
                  <div className="hirelane-job-form__field">
                    <label className="hirelane-job-form__label" htmlFor={`hirelane-cand-edit-${c.id}-designation`}>
                      Current designation
                    </label>
                    <input
                      id={`hirelane-cand-edit-${c.id}-designation`}
                      className="hirelane-job-form__input"
                      value={candidateDrafts[c.id]?.current_designation ?? ''}
                      onChange={(e) =>
                        setCandidateDrafts((prev) => ({
                          ...prev,
                          [c.id]: { ...prev[c.id], current_designation: e.target.value }
                        }))
                      }
                      placeholder="Role / title"
                    />
                  </div>
                </div>

                <div className="hirelane-job-form__optionalBlock">
                  <p className="hirelane-job-form__hint hirelane-detail__addOptionalHint">
                    Compensation, location, links, and experience (all optional).
                  </p>
                  <div className="hirelane-job-form__grid">
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">Current CTC (text)</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.current_ctc ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], current_ctc: e.target.value }
                          }))
                        }
                        placeholder="e.g. 18 LPA or brief description + currency"
                      />
                    </div>
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">Expected CTC (text)</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.expected_ctc ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], expected_ctc: e.target.value }
                          }))
                        }
                        placeholder="e.g. 22 LPA or range + currency"
                      />
                    </div>
                  </div>
                  <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">Notice period</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.notice_period ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], notice_period: e.target.value }
                          }))
                        }
                        placeholder="e.g. 60 days"
                      />
                    </div>
                    <label className="hirelane-job-form__checkRow">
                      <input
                        type="checkbox"
                        checked={!!candidateDrafts[c.id]?.notice_period_negotiable}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], notice_period_negotiable: e.target.checked }
                          }))
                        }
                      />
                      <span>Notice period negotiable</span>
                    </label>
                    <label className="hirelane-job-form__checkRow">
                      <input
                        type="checkbox"
                        checked={!!candidateDrafts[c.id]?.currently_on_notice}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], currently_on_notice: e.target.checked }
                          }))
                        }
                      />
                      <span>Currently serving notice</span>
                    </label>
                  </div>
                  <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">Current location</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.current_location ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], current_location: e.target.value }
                          }))
                        }
                        placeholder="City / region"
                      />
                    </div>
                    <label className="hirelane-job-form__checkRow">
                      <input
                        type="checkbox"
                        checked={!!candidateDrafts[c.id]?.willing_to_relocate}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], willing_to_relocate: e.target.checked }
                          }))
                        }
                      />
                      <span>Willing to relocate</span>
                    </label>
                  </div>
                  <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                    <span className="hirelane-job-form__label">Work mode preference</span>
                    <select
                      className="hirelane-job-form__select"
                      value={candidateDrafts[c.id]?.work_mode_preference ?? ''}
                      onChange={(e) =>
                        setCandidateDrafts((prev) => ({
                          ...prev,
                          [c.id]: { ...prev[c.id], work_mode_preference: e.target.value }
                        }))
                      }
                    >
                      <option value="">No preference</option>
                      <option value="office">Office</option>
                      <option value="remote">Remote</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="either">Either</option>
                    </select>
                  </div>
                  <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">LinkedIn</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.linkedin_url ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], linkedin_url: e.target.value }
                          }))
                        }
                        placeholder="Profile URL"
                      />
                    </div>
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">GitHub</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.github_url ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], github_url: e.target.value }
                          }))
                        }
                        placeholder="Profile URL"
                      />
                    </div>
                  </div>
                  <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">Total experience (years)</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.total_experience ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], total_experience: e.target.value }
                          }))
                        }
                        placeholder="Years"
                        inputMode="decimal"
                      />
                    </div>
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">Relevant experience (years)</span>
                      <input
                        className="hirelane-job-form__input"
                        value={candidateDrafts[c.id]?.relevant_experience ?? ''}
                        onChange={(e) =>
                          setCandidateDrafts((prev) => ({
                            ...prev,
                            [c.id]: { ...prev[c.id], relevant_experience: e.target.value }
                          }))
                        }
                        placeholder="Years"
                        inputMode="decimal"
                      />
                    </div>
                  </div>
                </div>

                <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                  <span className="hirelane-job-form__label">Skills</span>
                  <textarea
                    className="hirelane-job-form__textarea"
                    value={candidateDrafts[c.id]?.skills ?? ''}
                    onChange={(e) =>
                      setCandidateDrafts((prev) => ({ ...prev, [c.id]: { ...prev[c.id], skills: e.target.value } }))
                    }
                    placeholder="Key skills and technologies"
                    rows={2}
                  />
                </div>
                <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                  <span className="hirelane-job-form__label">Notes</span>
                  <textarea
                    className="hirelane-job-form__textarea"
                    value={candidateDrafts[c.id]?.notes ?? ''}
                    onChange={(e) =>
                      setCandidateDrafts((prev) => ({ ...prev, [c.id]: { ...prev[c.id], notes: e.target.value } }))
                    }
                    placeholder="Internal notes"
                    rows={2}
                  />
                </div>

                <div className="hirelane-job-form__actionsRow">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={cancelEditCandidate}
                    disabled={savingCandidateId === c.id}
                  >
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => saveCandidateDetails(c.id)} disabled={savingCandidateId === c.id}>
                    {savingCandidateId === c.id ? 'Saving…' : 'Save changes'}
                  </Button>
                </div>
              </section>
            </div>
          </div>
        )}

        <details className="hirelane-clarif-disclosure hirelane-clarif-disclosure--card">
          <summary className="hirelane-clarif-disclosure__summary">
            <span className="hirelane-clarif-disclosure__chevron" aria-hidden="true">
              ›
            </span>
            <span className="hirelane-clarif-disclosure__summaryText">
              <span className="hirelane-clarif-disclosure__titleLine">
                <span className="hirelane-clarif-disclosure__title">Clarity Loop</span>
                <span
                  className={`hirelane-clarif-disclosure__titleLineStats${!clarifSt ? ' hirelane-clarif-disclosure__titleLineStats--loading' : ''}`}
                  role="group"
                  aria-label={
                    clarifSt
                      ? `Profile ${clarifSt.profile_completeness_pct}% complete on tracked fields. ${clarifSt.record_gaps} missing on record. ${clarifSt.custom_prompts_open} custom questions open on active loops.`
                      : 'Loading Clarity Loop summary'
                  }
                >
                  {!clarifSt ? (
                    <span className="hirelane-clarif-disclosure__titleLineLoading">…</span>
                  ) : (
                    <>
                      <div
                        className="hirelane-clarif-disclosure__titleLineMeterTrack"
                        title={`Profile completeness: ${clarifSt.profile_completeness_pct}% of ${clarifSt.profile_slots_total} tracked field${clarifSt.profile_slots_total === 1 ? '' : 's'} filled (same rules as Clarity Loop auto-questions).`}
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={clarifSt.profile_completeness_pct}
                        aria-valuetext={`${clarifSt.profile_completeness_pct}% complete`}
                      >
                        <div
                          className="hirelane-clarif-disclosure__titleLineMeterFill"
                          style={{ width: `${clarifSt.profile_completeness_pct}%` }}
                        />
                      </div>
                      <span className="hirelane-clarif-disclosure__titleLinePct">{clarifSt.profile_completeness_pct}%</span>
                      <span className="hirelane-clarif-disclosure__titleLineSep" aria-hidden="true">
                        ·
                      </span>
                      <span
                        title="Standard candidate fields still empty that we would ask about in a Clarity Loop."
                      >
                        {clarifSt.record_gaps === 0 ? 'Complete' : `${clarifSt.record_gaps} missing`}
                      </span>
                      <span className="hirelane-clarif-disclosure__titleLineSep" aria-hidden="true">
                        ·
                      </span>
                      <span
                        title='Questions you added with "Add question" still pending or in progress on an active loop.'
                      >
                        {clarifSt.custom_prompts_open === 0
                          ? '0 custom'
                          : `${clarifSt.custom_prompts_open} custom`}
                      </span>
                    </>
                  )}
                </span>
              </span>
            </span>
          </summary>
          <ClarificationSection
            candidateId={c.id}
            onToast={setPageToast}
            onSessionQuestionsUpdated={handleClarificationQuestionsUpdated}
          />
        </details>
        <CandidateInterviewTimeline
          candidateId={c.id}
          refreshKey={timelineRefreshKey}
          className="hirelane-detail__timelineDisclosure--belowClarifications"
        />
      </div>
    );
  };

  if (loading) return <BunnyLoader />;

  return (
    <div className="hirelane-detail">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>{job?.title || 'Job'}</h2>
          <div style={{ opacity: 0.85, marginTop: 4 }}>
            {job?.department || '—'} • {job?.status || '—'} •{' '}
            <strong>
              {tabCounts.review} review · {tabCounts.shortlisted} shortlisted · {tabCounts.interview} scheduled ·{' '}
              {tabCounts.selected} selected · {tabCounts.rejected} rejected
            </strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          <Button onClick={() => navigate(`/hirelane/jobs/${jobId}/edit`)}>Edit job</Button>
          <Button variant="secondary" onClick={() => navigate('/hirelane')}>
            Back
          </Button>
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 16, padding: 12, border: '1px solid #ffb3b3', background: '#fff5f5' }}>
          {error}
        </div>
      )}

      <section className="hirelane-detail__jobBrief">
        {!jobBriefExpanded ? (
          <div className="hirelane-detail__jobBriefCollapsed">
            <button
              type="button"
              className="hirelane-detail__jobBriefCollapsedMain"
              onClick={expandJobBriefReadOnly}
              aria-expanded="false"
            >
              <div className="hirelane-detail__jobBriefCollapsedTitleRow">
                <span className="hirelane-detail__jobBriefCollapsedHeading">Job brief</span>
                {jobBriefDirty && (
                  <span className="hirelane-detail__jobBriefDirtyBadge" title="Unsaved changes">
                    Unsaved
                  </span>
                )}
              </div>
              <p className="hirelane-detail__jobBriefCollapsedPreview">{jobBriefPreviewLine(jobDraft)}</p>
              <p className="hirelane-detail__jobBriefCollapsedHint">
                Cony review uses the <strong>final job description</strong>. Click to expand or use Edit.
              </p>
            </button>
            <div className="hirelane-detail__jobBriefCollapsedAside">
              <Button type="button" variant="secondary" onClick={startJobBriefEdit}>
                Edit
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="hirelane-detail__jobBriefExpandedHeader">
              <h3 style={{ margin: 0 }}>Job brief</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <button type="button" className="hirelane-detail__jobBriefChevronBtn" onClick={collapseJobBrief}>
                  Collapse
                </button>
                {!jobBriefEditing ? (
                  <Button type="button" variant="secondary" onClick={startJobBriefEdit}>
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button type="button" variant="secondary" onClick={exitJobBriefEdit}>
                      Cancel edit
                    </Button>
                    <Button variant="secondary" type="button" onClick={resetJobDraft} disabled={!jobBriefDirty || savingJob}>
                      Reset
                    </Button>
                    <Button type="button" onClick={saveJobBrief} disabled={!jobBriefDirty || savingJob}>
                      {savingJob ? 'Saving…' : 'Save job'}
                    </Button>
                  </>
                )}
              </div>
            </div>
            <p style={{ margin: '8px 0 12px', fontSize: 13, opacity: 0.85 }}>
              {jobBriefEditing ? (
                <>
                  Editing — changes affect Cony review after <strong>Save job</strong>.
                </>
              ) : (
                <>
                  View mode — select <strong>Edit</strong> to change fields.
                </>
              )}
            </p>
            {jobBriefDirty && (
              <div style={{ marginBottom: 10, fontSize: 13, fontWeight: 600, color: '#b45309' }}>Unsaved changes</div>
            )}
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Final job description</label>
            {jobBriefEditing ? (
              <textarea
                value={jobDraft.final_jd}
                onChange={(e) => setJobDraft((d) => ({ ...d, final_jd: e.target.value }))}
                placeholder="Full job description used for Cony matching…"
                rows={10}
                style={{ padding: 10, width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
              />
            ) : (
              <div
                className="hirelane-detail__jobBriefReadOnly"
                style={{ marginBottom: 12 }}
              >
                {jobDraft.final_jd?.trim() ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{jobDraft.final_jd}</div>
                ) : (
                  <span style={{ opacity: 0.65 }}>No job description yet.</span>
                )}
              </div>
            )}
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>
              Key requirements / skills
            </label>
            {jobBriefEditing ? (
              <textarea
                value={jobDraft.key_skills}
                onChange={(e) => setJobDraft((d) => ({ ...d, key_skills: e.target.value }))}
                placeholder="Must-have skills, stack, certifications…"
                rows={4}
                style={{ padding: 10, width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
              />
            ) : (
              <div className="hirelane-detail__jobBriefReadOnly" style={{ marginBottom: 12 }}>
                {jobDraft.key_skills?.trim() ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{jobDraft.key_skills}</div>
                ) : (
                  <span style={{ opacity: 0.65 }}>None specified.</span>
                )}
              </div>
            )}
            <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Responsibilities</label>
            {jobBriefEditing ? (
              <textarea
                value={jobDraft.responsibilities}
                onChange={(e) => setJobDraft((d) => ({ ...d, responsibilities: e.target.value }))}
                placeholder="Core responsibilities and expectations…"
                rows={5}
                style={{ padding: 10, width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
              />
            ) : (
              <div className="hirelane-detail__jobBriefReadOnly">
                {jobDraft.responsibilities?.trim() ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{jobDraft.responsibilities}</div>
                ) : (
                  <span style={{ opacity: 0.65 }}>None specified.</span>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <div className="hirelane-detail__tabsRow">
        <div className="hirelane-detail__tabs hirelane-detail__tabs--many">
          <button
            type="button"
            className={`hirelane-detail__tab ${workflowTab === TAB_IDS.review ? 'hirelane-detail__tab--active' : ''}`}
            onClick={() => setWorkflowTab(TAB_IDS.review)}
          >
            Review ({tabCounts.review})
          </button>
          <button
            type="button"
            className={`hirelane-detail__tab ${workflowTab === TAB_IDS.shortlisted ? 'hirelane-detail__tab--active' : ''}`}
            onClick={() => setWorkflowTab(TAB_IDS.shortlisted)}
          >
            Shortlisted ({tabCounts.shortlisted})
          </button>
          <button
            type="button"
            className={`hirelane-detail__tab ${workflowTab === TAB_IDS.interview ? 'hirelane-detail__tab--active' : ''}`}
            onClick={() => setWorkflowTab(TAB_IDS.interview)}
          >
            Interview ({tabCounts.interview})
          </button>
          <button
            type="button"
            className={`hirelane-detail__tab ${workflowTab === TAB_IDS.selected ? 'hirelane-detail__tab--active' : ''}`}
            onClick={() => setWorkflowTab(TAB_IDS.selected)}
          >
            Selected ({tabCounts.selected})
          </button>
          <button
            type="button"
            className={`hirelane-detail__tab ${workflowTab === TAB_IDS.rejected ? 'hirelane-detail__tab--active' : ''}`}
            onClick={() => setWorkflowTab(TAB_IDS.rejected)}
          >
            Rejected ({tabCounts.rejected})
          </button>
        </div>
        <div className="hirelane-detail__tabsActions">
          <Button
            type="button"
            onClick={() => {
              setAddCandidateMode('manual');
              setAddCandidateModalOpen(true);
            }}
          >
            Add candidate
          </Button>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        {shownList.length > 0 && (
          <p className="hirelane-detail__toolbarHint">
            Use tabs to focus who needs profile review, who is ready for the next action (Shortlisted), who has an
            interview scheduled, and closed outcomes. Shortlist and reject require reasons and comments; scheduling records
            rounds automatically.
          </p>
        )}
        {shownList.length === 0 ? (
          <div style={{ padding: 16, opacity: 0.85 }}>
            {workflowTab === TAB_IDS.review && 'No candidates in the review queue.'}
            {workflowTab === TAB_IDS.shortlisted && 'No shortlisted candidates yet.'}
            {workflowTab === TAB_IDS.interview && 'No interviews scheduled.'}
            {workflowTab === TAB_IDS.selected && 'No selected candidates yet.'}
            {workflowTab === TAB_IDS.rejected && 'No rejected candidates.'}
          </div>
        ) : (
          <div className="hirelane-detail__cardList">{shownList.map((c) => renderCandidateCard(c))}</div>
        )}
      </div>

      {profilePanelCandidate && (
        <>
          <div
            className="hirelane-detail__profileSlideBackdrop"
            onClick={closeProfilePanel}
            aria-hidden
          />
          <aside
            className="hirelane-detail__profileSlideOver"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hirelane-profile-panel-title"
          >
            <header className="hirelane-detail__profileSlideOverHeader">
              <div className="hirelane-detail__profileSlideOverTitleWrap">
                <h2 id="hirelane-profile-panel-title" className="hirelane-detail__profileSlideOverTitle">
                  {profilePanelCandidate.name}
                </h2>
                <span
                  className="hirelane-detail__statusPill hirelane-detail__profileSlideOverStage"
                  style={{
                    background:
                      (STATUS_ACCENTS[profilePanelCandidate.status] || STATUS_ACCENTS.New).pillBg,
                    color: (STATUS_ACCENTS[profilePanelCandidate.status] || STATUS_ACCENTS.New).pillFg
                  }}
                >
                  {hirelanePipelineStageLabel(profilePanelCandidate.status)}
                </span>
              </div>
              <button
                type="button"
                className="hirelane-detail__profileSlideOverClose"
                onClick={closeProfilePanel}
                aria-label="Close profile"
              >
                ×
              </button>
            </header>
            <div className="hirelane-detail__profileSlideOverBody">
              <p className="hirelane-detail__profileSlideOverHint">Read-only profile</p>
              <CandidateProfileDetails
                candidate={profilePanelCandidate}
                vendors={vendors}
                hasResume={!!profilePanelCandidate.resume_file_path}
                onViewResume={() => viewResume(profilePanelCandidate.id)}
                onEdit={() => beginEditCandidate(profilePanelCandidate)}
              />
            </div>
          </aside>
        </>
      )}

      {statusDecisionModal && (
        <>
          <div
            className="hirelane-modal-backdrop"
            onClick={closeStatusDecisionModal}
            aria-hidden
          />
          <div
            className="hirelane-modal hirelane-detail__statusDecisionModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hirelane-status-decision-title"
          >
            <div className="hirelane-modal__header">
              <div id="hirelane-status-decision-title" style={{ fontWeight: 700, fontSize: 16 }}>
                {statusDecisionModal.mode === 'reject'
                  ? 'Reject Candidate'
                  : statusDecisionModal.mode === 'shortlist'
                    ? 'Shortlist Candidate'
                    : statusDecisionModal.mode === 'select'
                      ? 'Mark selected'
                      : 'Put on hold'}
              </div>
              <Button type="button" variant="secondary" onClick={closeStatusDecisionModal}>
                Close
              </Button>
            </div>
            <div className="hirelane-modal__body hirelane-detail__statusDecisionBody">
              <p className="hirelane-detail__statusDecisionLead">
                {statusDecisionModal.mode === 'reject' ? (
                  <>You are about to reject </>
                ) : statusDecisionModal.mode === 'shortlist' ? (
                  <>You are about to shortlist </>
                ) : statusDecisionModal.mode === 'select' ? (
                  <>Final selection for </>
                ) : (
                  <>Hold (pipeline) for </>
                )}
                <strong>{statusDecisionModal.candidateName}</strong>. Current stage:{' '}
                <strong>{hirelanePipelineStageLabel(statusDecisionModal.previousStatus)}</strong>.
              </p>
              {(statusDecisionModal.mode === 'reject' || statusDecisionModal.mode === 'shortlist') && (
                <label className="hirelane-detail__statusDecisionLabel">
                  {statusDecisionModal.mode === 'reject' ? 'Rejection reason' : 'Shortlist reason'}
                  <select
                    className="hirelane-detail__statusDecisionSelect"
                    value={decisionForm.reason}
                    onChange={(e) => setDecisionForm((f) => ({ ...f, reason: e.target.value }))}
                    aria-required
                  >
                    <option value="">Select a reason…</option>
                    {(statusDecisionModal.mode === 'reject'
                      ? HIRELANE_REJECT_REASONS
                      : HIRELANE_SHORTLIST_REASONS
                    ).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="hirelane-detail__statusDecisionLabel">
                Comments
                <textarea
                  className="hirelane-detail__statusDecisionTextarea"
                  value={decisionForm.comments}
                  onChange={(e) => setDecisionForm((f) => ({ ...f, comments: e.target.value }))}
                  placeholder={
                    statusDecisionModal.mode === 'reject'
                      ? 'Add specific reason for rejection…'
                      : statusDecisionModal.mode === 'shortlist'
                        ? 'Add why this candidate is being shortlisted…'
                        : statusDecisionModal.mode === 'select'
                          ? 'Document the selection decision…'
                          : 'Explain the hold and next check-in expectations…'
                  }
                  rows={4}
                  aria-required
                />
              </label>
              {statusDecisionModal.mode === 'shortlist' ? (
                <label className="hirelane-detail__statusDecisionLabel">
                  Recommended next step
                  <select
                    className="hirelane-detail__statusDecisionSelect"
                    value={decisionForm.recommended_next_step}
                    onChange={(e) =>
                      setDecisionForm((f) => ({ ...f, recommended_next_step: e.target.value }))
                    }
                    aria-required
                  >
                    <option value="">Select next step…</option>
                    {HIRELANE_SHORTLIST_NEXT_STEPS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <div className="hirelane-detail__statusDecisionActions">
                <Button type="button" variant="secondary" onClick={closeStatusDecisionModal}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={confirmStatusDecision}
                  disabled={!decisionFormValid || pipelineBusyId === statusDecisionModal.candidateId}
                  className={
                    statusDecisionModal.mode === 'reject' ? 'hirelane-detail__confirmRejectBtn' : ''
                  }
                >
                  {pipelineBusyId === statusDecisionModal.candidateId
                    ? 'Saving…'
                    : statusDecisionModal.mode === 'reject'
                      ? 'Confirm Reject'
                      : statusDecisionModal.mode === 'shortlist'
                        ? 'Confirm Shortlist'
                        : statusDecisionModal.mode === 'select'
                          ? 'Confirm selected'
                          : 'Confirm hold'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {scheduleModal?.candidate && (
        <ScheduleInterviewModal
          key={`sched-${scheduleModal.candidate.id}`}
          candidateName={scheduleModal.candidate.name || 'Candidate'}
          duplicateExisting={null}
          predefinedInterviewers={interviewers}
          onClose={() => setScheduleModal(null)}
          onSubmit={(payload) => submitScheduleInterview(payload)}
          submitting={scheduleSubmitting}
        />
      )}

      {rescheduleModal?.candidate && rescheduleModal?.interview && (
        <ScheduleInterviewModal
          key={`resched-${rescheduleModal.interview.id}`}
          candidateName={rescheduleModal.candidate.name || 'Candidate'}
          existingInterview={rescheduleModal.interview}
          predefinedInterviewers={interviewers}
          onClose={() => setRescheduleModal(null)}
          onSubmit={(payload) => submitRescheduleInterview(payload)}
          submitting={scheduleSubmitting}
        />
      )}

      {outcomeModal?.candidate && outcomeModal?.interview && (
        <InterviewOutcomeModal
          roundLabel={outcomeModal.interview.display_round_label}
          onClose={() => setOutcomeModal(null)}
          onSubmit={(payload) => submitInterviewOutcome(payload)}
          submitting={scheduleSubmitting}
        />
      )}

      {addCandidateModalOpen && (
        <>
          <div
            className="hirelane-modal-backdrop"
            onClick={() => setAddCandidateModalOpen(false)}
            aria-hidden
          />
          <div
            className="hirelane-modal hirelane-modal--hirelane"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hirelane-add-candidate-title"
          >
            <div className="hirelane-modal__header hirelane-modal__header--hirelane">
              <div className="hirelane-modal__headerBrand">
                <p className="hirelane-modal__kicker">Hirelane</p>
                <h2 className="hirelane-modal__heading" id="hirelane-add-candidate-title">
                  Add candidate
                </h2>
              </div>
              <Button type="button" variant="secondary" onClick={() => setAddCandidateModalOpen(false)}>
                Close
              </Button>
            </div>
            <div className="hirelane-modal__tabs hirelane-modal__tabs--hirelane" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={addCandidateMode === 'manual'}
                className={`hirelane-modal__tab hirelane-modal__tab--hirelane ${
                  addCandidateMode === 'manual' ? 'hirelane-modal__tab--active' : ''
                }`}
                onClick={() => setAddCandidateMode('manual')}
              >
                Manual entry
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={addCandidateMode === 'resume'}
                className={`hirelane-modal__tab hirelane-modal__tab--hirelane ${
                  addCandidateMode === 'resume' ? 'hirelane-modal__tab--active' : ''
                }`}
                onClick={() => setAddCandidateMode('resume')}
              >
                Résumé only (Cony extract)
              </button>
            </div>
            <div className="hirelane-modal__body hirelane-modal__body--hirelane">
              <section className="hirelane-job-form__panel" style={{ marginBottom: 16 }}>
                <span className="hirelane-job-form__label">Profile source</span>
                <p className="hirelane-job-form__hint" style={{ marginTop: 4, marginBottom: 10 }}>
                  Who supplied this profile: your team (direct), a vendor, or a referral.
                </p>
                <div className="hirelane-job-form__segmented" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <Button
                    type="button"
                    variant={newCandProfileSource === 'direct' ? undefined : 'secondary'}
                    onClick={() => {
                      setNewCandProfileSource('direct');
                      setNewCandVendorId('');
                      setNewCandReferrerName('');
                    }}
                  >
                    Direct
                  </Button>
                  <Button
                    type="button"
                    variant={newCandProfileSource === 'vendor' ? undefined : 'secondary'}
                    onClick={() => {
                      setNewCandProfileSource('vendor');
                      setNewCandReferrerName('');
                    }}
                  >
                    Vendor
                  </Button>
                  <Button
                    type="button"
                    variant={newCandProfileSource === 'referral' ? undefined : 'secondary'}
                    onClick={() => {
                      setNewCandProfileSource('referral');
                      setNewCandVendorId('');
                    }}
                  >
                    Referral
                  </Button>
                </div>
                {newCandProfileSource === 'vendor' ? (
                  <div className="hirelane-job-form__field hirelane-job-form__field--stack" style={{ marginTop: 12 }}>
                    <span className="hirelane-job-form__label">Vendor</span>
                    {vendors.length === 0 ? (
                      <p className="hirelane-job-form__hint">
                        No vendors yet. Open the{' '}
                        <button
                          type="button"
                          className="hirelane-detail__textLink"
                          onClick={() => {
                            setAddCandidateModalOpen(false);
                            navigate('/hirelane');
                          }}
                        >
                          Hiring dashboard
                        </button>{' '}
                        and use <strong>Manage vendors</strong>.
                      </p>
                    ) : (
                      <select
                        className="hirelane-job-form__input"
                        value={newCandVendorId}
                        onChange={(e) => setNewCandVendorId(e.target.value)}
                        aria-label="Vendor"
                      >
                        <option value="">Select vendor…</option>
                        {vendors.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ) : null}
                {newCandProfileSource === 'referral' ? (
                  <div className="hirelane-job-form__field hirelane-job-form__field--stack" style={{ marginTop: 12 }}>
                    <span className="hirelane-job-form__label">Referrer name</span>
                    <input
                      className="hirelane-job-form__input"
                      value={newCandReferrerName}
                      onChange={(e) => setNewCandReferrerName(e.target.value)}
                      placeholder="Who referred this candidate?"
                      autoComplete="off"
                    />
                  </div>
                ) : null}
              </section>
              {addCandidateMode === 'manual' ? (
                <div className="hirelane-job-form__stack">
                  <section className="hirelane-job-form__panel">
                    <p className="hirelane-job-form__lede">
                      Enter contact details and optionally attach a résumé. Save the candidate first, then run a{' '}
                      <strong>Cony review</strong> from their row.
                    </p>
                    <div className="hirelane-job-form__field">
                      <span className="hirelane-job-form__label">Candidate name</span>
                      <input
                        className="hirelane-job-form__input"
                        value={manual.name}
                        onChange={(e) => setManual((m) => ({ ...m, name: e.target.value }))}
                        placeholder="Full name"
                        autoComplete="off"
                      />
                    </div>
                    <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                      <div className="hirelane-job-form__field">
                        <span className="hirelane-job-form__label">Email</span>
                        <input
                          className="hirelane-job-form__input"
                          value={manual.email}
                          onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))}
                          placeholder="Email"
                          autoComplete="off"
                        />
                      </div>
                      <div className="hirelane-job-form__field">
                        <span className="hirelane-job-form__label">Phone</span>
                        <input
                          className="hirelane-job-form__input"
                          value={manual.phone}
                          onChange={(e) => setManual((m) => ({ ...m, phone: e.target.value }))}
                          placeholder="Phone"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                    <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                      <span className="hirelane-job-form__label">Skills</span>
                      <textarea
                        className="hirelane-job-form__textarea"
                        value={manual.skills}
                        onChange={(e) => setManual((m) => ({ ...m, skills: e.target.value }))}
                        placeholder="Key skills and technologies"
                        rows={2}
                      />
                    </div>
                    <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                      <span className="hirelane-job-form__label">Notes</span>
                      <textarea
                        className="hirelane-job-form__textarea"
                        value={manual.notes}
                        onChange={(e) => setManual((m) => ({ ...m, notes: e.target.value }))}
                        placeholder="Internal notes"
                        rows={2}
                      />
                    </div>
                    <div className="hirelane-job-form__segmented hirelane-job-form__field--stack">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setManualShowMore((v) => !v)}
                        disabled={savingManual}
                      >
                        {manualShowMore ? 'Hide optional fields' : 'Show more'}
                      </Button>
                    </div>

                    {manualShowMore ? (
                      <div className="hirelane-job-form__optionalBlock">
                        <p className="hirelane-job-form__hint hirelane-detail__addOptionalHint">
                          Compensation, location, links, and experience (all optional).
                        </p>
                        <div className="hirelane-job-form__grid">
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">Current CTC (text)</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.current_ctc}
                              onChange={(e) => setManual((m) => ({ ...m, current_ctc: e.target.value }))}
                              placeholder="e.g. 18 LPA or brief description + currency"
                            />
                          </div>
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">Expected CTC (text)</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.expected_ctc}
                              onChange={(e) => setManual((m) => ({ ...m, expected_ctc: e.target.value }))}
                              placeholder="e.g. 22 LPA or range + currency"
                            />
                          </div>
                        </div>
                        <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">Notice period</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.notice_period}
                              onChange={(e) => setManual((m) => ({ ...m, notice_period: e.target.value }))}
                              placeholder="e.g. 60 days"
                            />
                          </div>
                          <label className="hirelane-job-form__checkRow">
                            <input
                              type="checkbox"
                              checked={!!manual.notice_period_negotiable}
                              onChange={(e) =>
                                setManual((m) => ({ ...m, notice_period_negotiable: e.target.checked }))
                              }
                            />
                            <span>Notice period negotiable</span>
                          </label>
                          <label className="hirelane-job-form__checkRow">
                            <input
                              type="checkbox"
                              checked={!!manual.currently_on_notice}
                              onChange={(e) =>
                                setManual((m) => ({ ...m, currently_on_notice: e.target.checked }))
                              }
                            />
                            <span>Currently serving notice</span>
                          </label>
                        </div>
                        <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">Current location</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.current_location}
                              onChange={(e) => setManual((m) => ({ ...m, current_location: e.target.value }))}
                              placeholder="City / region"
                            />
                          </div>
                          <label className="hirelane-job-form__checkRow">
                            <input
                              type="checkbox"
                              checked={!!manual.willing_to_relocate}
                              onChange={(e) => setManual((m) => ({ ...m, willing_to_relocate: e.target.checked }))}
                            />
                            <span>Willing to relocate</span>
                          </label>
                        </div>
                        <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                          <span className="hirelane-job-form__label">Work mode preference</span>
                          <select
                            className="hirelane-job-form__select"
                            value={manual.work_mode_preference}
                            onChange={(e) => setManual((m) => ({ ...m, work_mode_preference: e.target.value }))}
                          >
                            <option value="">No preference</option>
                            <option value="office">Office</option>
                            <option value="remote">Remote</option>
                            <option value="hybrid">Hybrid</option>
                            <option value="either">Either</option>
                          </select>
                        </div>
                        <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">LinkedIn</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.linkedin_url}
                              onChange={(e) => setManual((m) => ({ ...m, linkedin_url: e.target.value }))}
                              placeholder="Profile URL"
                            />
                          </div>
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">GitHub</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.github_url}
                              onChange={(e) => setManual((m) => ({ ...m, github_url: e.target.value }))}
                              placeholder="Profile URL"
                            />
                          </div>
                        </div>
                        <div className="hirelane-job-form__grid hirelane-job-form__field--stack">
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">Total experience (years)</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.total_experience}
                              onChange={(e) => setManual((m) => ({ ...m, total_experience: e.target.value }))}
                              placeholder="Years"
                              inputMode="decimal"
                            />
                          </div>
                          <div className="hirelane-job-form__field">
                            <span className="hirelane-job-form__label">Relevant experience (years)</span>
                            <input
                              className="hirelane-job-form__input"
                              value={manual.relevant_experience}
                              onChange={(e) => setManual((m) => ({ ...m, relevant_experience: e.target.value }))}
                              placeholder="Years"
                              inputMode="decimal"
                            />
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                      <span className="hirelane-job-form__label">Résumé / profile (optional)</span>
                      <input
                        type="file"
                        accept=".pdf,.docx,.txt,application/pdf,text/plain"
                        onChange={(e) => setManualResumeFile(e.target.files?.[0] || null)}
                      />
                      <p className="hirelane-job-form__fileHint">
                        PDF, DOCX, or TXT. Stored securely; text is extracted for Cony review.
                      </p>
                    </div>
                    <div className="hirelane-job-form__actionsRow">
                      <Button onClick={addManual} disabled={!manual.name.trim() || savingManual}>
                        {savingManual ? 'Saving…' : 'Save candidate'}
                      </Button>
                    </div>
                  </section>
                </div>
              ) : (
                <div className="hirelane-job-form__stack">
                  <section className="hirelane-job-form__panel">
                    <p className="hirelane-job-form__lede">
                      Upload a résumé and run Cony extraction. Review the draft fields, edit if needed, then confirm to
                      save.
                    </p>
                    <div className="hirelane-job-form__segmented">
                      <input
                        type="file"
                        accept=".pdf,.docx,.txt,application/pdf,text/plain"
                        onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                      />
                      <Button variant="secondary" onClick={extract} disabled={!resumeFile || extracting}>
                        {extracting ? 'Extracting…' : 'Extract with Cony'}
                      </Button>
                    </div>
                  </section>

                  {draft ? (
                    <section className="hirelane-job-form__panel">
                      <h3 className="hirelane-job-form__sectionTitle">Review extracted draft</h3>
                      <div className="hirelane-job-form__grid">
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">Name</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.name || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                            placeholder="Full name"
                            autoComplete="off"
                          />
                        </div>
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">Email</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.email || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
                            placeholder="Email"
                            autoComplete="off"
                          />
                        </div>
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">Phone</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.phone || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                            placeholder="Phone"
                            autoComplete="off"
                          />
                        </div>
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">Current company</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.current_company || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, current_company: e.target.value }))}
                            placeholder="Company"
                            autoComplete="off"
                          />
                        </div>
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">Current designation</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.current_designation || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, current_designation: e.target.value }))}
                            placeholder="Title"
                            autoComplete="off"
                          />
                        </div>
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">Location</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.current_location || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, current_location: e.target.value }))}
                            placeholder="City / region"
                            autoComplete="off"
                          />
                        </div>
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">LinkedIn URL</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.linkedin_url || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, linkedin_url: e.target.value }))}
                            placeholder="https://linkedin.com/in/…"
                            autoComplete="off"
                          />
                        </div>
                        <div className="hirelane-job-form__field">
                          <span className="hirelane-job-form__label">GitHub URL</span>
                          <input
                            className="hirelane-job-form__input"
                            value={draft.github_url || ''}
                            onChange={(e) => setDraft((d) => ({ ...d, github_url: e.target.value }))}
                            placeholder="https://github.com/…"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                        <span className="hirelane-job-form__label">Skills</span>
                        <textarea
                          className="hirelane-job-form__textarea"
                          value={draft.skills || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, skills: e.target.value }))}
                          placeholder="Skills"
                          rows={3}
                        />
                      </div>
                      <div className="hirelane-job-form__field hirelane-job-form__field--stack">
                        <span className="hirelane-job-form__label">Candidate summary</span>
                        <textarea
                          className="hirelane-job-form__textarea"
                          value={draft.candidate_summary || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, candidate_summary: e.target.value }))}
                          placeholder="Summary"
                          rows={3}
                        />
                      </div>
                      <div className="hirelane-job-form__actionsRow">
                        <Button variant="secondary" onClick={() => setDraft(null)} disabled={savingDraft}>
                          Discard
                        </Button>
                        <Button onClick={saveDraft} disabled={savingDraft || !draft.name?.trim()}>
                          {savingDraft ? 'Saving…' : 'Confirm & save candidate'}
                        </Button>
                      </div>
                    </section>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {pageToast ? (
        <div
          className={`hirelane-detail__pageToast hirelane-detail__pageToast--${pageToast.type}`}
          role="status"
        >
          {pageToast.message}
        </div>
      ) : null}
    </div>
  );
}
